import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
    Trophy, Users, Calendar, Clock, ChevronRight, ChevronDown, ChevronUp,
    Check, X, AlertCircle, Medal, Crown, TrendingUp, TrendingDown, Minus,
    RefreshCw, Share2, Copy, Star, Target, Lightbulb, Award, HelpCircle, Info,
    Play, Timer, Sparkles, BarChart3, ListOrdered, Zap, Lock, Bot, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { parseISO, differenceInSeconds, format } from 'date-fns';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to convert decimal odds to fractional
const decimalToFractional = (decimal) => {
    if (!decimal || decimal <= 1) return 'EVS';
    
    const profit = decimal - 1;
    
    // Common fractional odds lookup
    const commonOdds = [
        { decimal: 1.10, fractional: '1/10' },
        { decimal: 1.20, fractional: '1/5' },
        { decimal: 1.25, fractional: '1/4' },
        { decimal: 1.33, fractional: '1/3' },
        { decimal: 1.40, fractional: '2/5' },
        { decimal: 1.50, fractional: '1/2' },
        { decimal: 1.57, fractional: '4/7' },
        { decimal: 1.62, fractional: '8/13' },
        { decimal: 1.67, fractional: '4/6' },
        { decimal: 1.73, fractional: '8/11' },
        { decimal: 1.80, fractional: '4/5' },
        { decimal: 1.83, fractional: '5/6' },
        { decimal: 1.91, fractional: '10/11' },
        { decimal: 2.00, fractional: 'EVS' },
        { decimal: 2.10, fractional: '11/10' },
        { decimal: 2.20, fractional: '6/5' },
        { decimal: 2.25, fractional: '5/4' },
        { decimal: 2.38, fractional: '11/8' },
        { decimal: 2.50, fractional: '6/4' },
        { decimal: 2.62, fractional: '13/8' },
        { decimal: 2.75, fractional: '7/4' },
        { decimal: 2.88, fractional: '15/8' },
        { decimal: 3.00, fractional: '2/1' },
        { decimal: 3.25, fractional: '9/4' },
        { decimal: 3.50, fractional: '5/2' },
        { decimal: 3.75, fractional: '11/4' },
        { decimal: 4.00, fractional: '3/1' },
        { decimal: 4.33, fractional: '10/3' },
        { decimal: 4.50, fractional: '7/2' },
        { decimal: 5.00, fractional: '4/1' },
        { decimal: 5.50, fractional: '9/2' },
        { decimal: 6.00, fractional: '5/1' },
        { decimal: 6.50, fractional: '11/2' },
        { decimal: 7.00, fractional: '6/1' },
        { decimal: 8.00, fractional: '7/1' },
        { decimal: 9.00, fractional: '8/1' },
        { decimal: 10.00, fractional: '9/1' },
        { decimal: 11.00, fractional: '10/1' },
        { decimal: 12.00, fractional: '11/1' },
        { decimal: 13.00, fractional: '12/1' },
        { decimal: 14.00, fractional: '13/1' },
        { decimal: 15.00, fractional: '14/1' },
        { decimal: 16.00, fractional: '15/1' },
        { decimal: 17.00, fractional: '16/1' },
        { decimal: 21.00, fractional: '20/1' },
        { decimal: 26.00, fractional: '25/1' },
        { decimal: 34.00, fractional: '33/1' },
        { decimal: 41.00, fractional: '40/1' },
        { decimal: 51.00, fractional: '50/1' },
        { decimal: 67.00, fractional: '66/1' },
        { decimal: 101.00, fractional: '100/1' },
    ];
    
    // Find closest match
    let closest = commonOdds[0];
    let minDiff = Math.abs(decimal - closest.decimal);
    
    for (const odds of commonOdds) {
        const diff = Math.abs(decimal - odds.decimal);
        if (diff < minDiff) {
            minDiff = diff;
            closest = odds;
        }
    }
    
    // If close enough to a common odd, use it
    if (minDiff < 0.15) {
        return closest.fractional;
    }
    
    // Otherwise calculate approximate fractional
    const numerator = Math.round(profit * 2) / 2;
    if (numerator === Math.floor(numerator)) {
        return `${Math.floor(numerator)}/1`;
    }
    return `${Math.round(profit * 2)}/2`;
};

// Helper function for ordinal suffixes (1st, 2nd, 3rd, 4th, etc.)
const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Winner Celebration Component - Shows when competition is complete
const WinnerCelebration = ({ winner, leaderboard, totalRaces, currentUser }) => {
    const [showConfetti, setShowConfetti] = useState(true);
    const isCurrentUserWinner = currentUser?.id === winner?.user_id;
    
    // Get top 3
    const top3 = leaderboard.slice(0, 3);
    const podiumOrder = [1, 0, 2]; // 2nd, 1st, 3rd for visual display
    
    // Pre-generate confetti positions (memoized to avoid re-renders)
    const confettiPieces = useMemo(() => {
        const colors = ['#FFD700', '#00D4AA', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3'];
        return [...Array(50)].map((_, i) => ({
            id: i,
            left: `${(i * 2) % 100}%`,
            delay: `${(i * 0.06) % 3}s`,
            duration: `${3 + (i % 3)}s`,
            color: colors[i % colors.length],
            rotation: `${(i * 7.2) % 360}deg`
        }));
    }, []);
    
    return (
        <div className="relative mb-8 overflow-hidden">
            {/* Confetti Animation */}
            {showConfetti && (
                <div className="absolute inset-0 pointer-events-none">
                    {confettiPieces.map((piece) => (
                        <div
                            key={piece.id}
                            className="absolute animate-confetti"
                            style={{
                                left: piece.left,
                                top: '-10px',
                                animationDelay: piece.delay,
                                animationDuration: piece.duration,
                            }}
                        >
                            <div 
                                className="w-2 h-2 rounded-sm"
                                style={{
                                    backgroundColor: piece.color,
                                    transform: `rotate(${piece.rotation})`
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}
            
            {/* Main Celebration Card */}
            <Card className="border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-background to-primary/5 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl -ml-24 -mb-24" />
                
                <CardContent className="relative py-8">
                    {/* Trophy and Title */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mb-4 shadow-lg shadow-amber-500/30">
                            <Trophy className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">
                            {isCurrentUserWinner ? (
                                <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                                    Congratulations, You Won!
                                </span>
                            ) : (
                                <span>Competition Complete!</span>
                            )}
                        </h2>
                        <p className="text-muted-foreground">
                            All {totalRaces} races have finished
                        </p>
                    </div>
                    
                    {/* Podium */}
                    <div className="flex items-end justify-center gap-2 md:gap-4 mb-6">
                        {podiumOrder.map((pos) => {
                            const entry = top3[pos];
                            if (!entry) return null;
                            const height = pos === 0 ? 'h-32' : pos === 1 ? 'h-24' : 'h-20';
                            const isUser = entry.user_id === currentUser?.id;
                            
                            return (
                                <div key={pos} className="flex flex-col items-center">
                                    {/* Avatar/Medal */}
                                    <div className={`relative mb-2 ${pos === 0 ? 'scale-110' : ''}`}>
                                        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-lg ${
                                            pos === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg shadow-amber-500/40' :
                                            pos === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                            'bg-gradient-to-br from-amber-600 to-amber-800 text-white'
                                        } ${isUser ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}>
                                            {entry.rank}
                                        </div>
                                        {pos === 0 && <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-6 text-amber-400" />}
                                    </div>
                                    
                                    {/* Name & Points */}
                                    <p className={`font-semibold text-sm md:text-base text-center ${isUser ? 'text-primary' : ''}`}>
                                        {entry.user_name}
                                    </p>
                                    <p className="font-data font-bold text-lg">{entry.total_points.toFixed(1)}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {entry.correct_picks}W / {entry.picks_made} picks
                                    </p>
                                    
                                    {/* Podium Block */}
                                    <div className={`${height} w-20 md:w-28 mt-2 rounded-t-lg ${
                                        pos === 0 ? 'bg-gradient-to-t from-amber-600 to-amber-400' :
                                        pos === 1 ? 'bg-gradient-to-t from-gray-500 to-gray-300' :
                                        'bg-gradient-to-t from-amber-800 to-amber-600'
                                    } flex items-start justify-center pt-2`}>
                                        <span className="text-white font-bold text-xl">
                                            {pos === 0 ? '1st' : pos === 1 ? '2nd' : '3rd'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                        <div className="text-center p-3 bg-muted/30 rounded-lg">
                            <p className="text-2xl font-bold font-data text-amber-400">{totalRaces}</p>
                            <p className="text-xs text-muted-foreground">Races</p>
                        </div>
                        <div className="text-center p-3 bg-muted/30 rounded-lg">
                            <p className="text-2xl font-bold font-data text-primary">{leaderboard.length}</p>
                            <p className="text-xs text-muted-foreground">Players</p>
                        </div>
                        <div className="text-center p-3 bg-muted/30 rounded-lg">
                            <p className="text-2xl font-bold font-data text-green-400">{winner?.total_points.toFixed(1)}</p>
                            <p className="text-xs text-muted-foreground">Top Score</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* CSS for confetti animation */}
            <style>{`
                @keyframes confetti {
                    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
                .animate-confetti {
                    animation: confetti linear forwards;
                }
            `}</style>
        </div>
    );
};

// Countdown Timer Component
const CountdownTimer = ({ targetTime }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const target = parseISO(targetTime);
        
        const updateTimer = () => {
            const now = new Date();
            const diff = differenceInSeconds(target, now);
            
            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setIsExpired(true);
                return;
            }
            
            const days = Math.floor(diff / 86400);
            const hours = Math.floor((diff % 86400) / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = diff % 60;
            
            setTimeLeft({ days, hours, minutes, seconds });
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [targetTime]);

    if (isExpired) {
        return (
            <Badge className="bg-amber-500/20 text-amber-500 animate-pulse">
                <Play className="h-3 w-3 mr-1" />
                Race Starting!
            </Badge>
        );
    }

    // Show days format if more than 24 hours, otherwise just hours
    const showDays = timeLeft.days > 0;

    return (
        <div className="flex gap-2">
            {showDays ? (
                // Days, Hours, Minutes, Seconds format
                [
                    { value: timeLeft.days, label: 'DAYS' },
                    { value: timeLeft.hours, label: 'HRS' },
                    { value: timeLeft.minutes, label: 'MIN' },
                    { value: timeLeft.seconds, label: 'SEC' },
                ].map((item, idx) => (
                    <div key={idx} className="text-center">
                        <div className="bg-primary/20 text-primary font-data font-bold text-lg px-2 py-1 rounded">
                            {String(item.value).padStart(2, '0')}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-medium mt-0.5">{item.label}</div>
                    </div>
                ))
            ) : (
                // Hours, Minutes, Seconds format
                [
                    { value: timeLeft.hours, label: 'HRS' },
                    { value: timeLeft.minutes, label: 'MIN' },
                    { value: timeLeft.seconds, label: 'SEC' },
                ].map((item, idx) => (
                    <div key={idx} className="text-center">
                        <div className="bg-primary/20 text-primary font-data font-bold text-lg px-2 py-1 rounded">
                            {String(item.value).padStart(2, '0')}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-medium mt-0.5">{item.label}</div>
                    </div>
                ))
            )}
        </div>
    );
};

// How Scoring Works Component - Beginner-friendly explanation
const ScoringRulesContent = () => (
    <>
        {/* Simple Overview */}
                        <div className="p-3 bg-gradient-to-r from-green-500/20 to-green-500/5 rounded-lg border border-green-500/30">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <span className="text-lg">🎯</span>
                                The Simple Version
                            </h4>
                            <p className="text-sm text-muted-foreground">
                                <strong className="text-green-500">Points = Virtual Profit</strong>. Pick horses, earn points based on their finishing odds. 
                                The longer the odds, the more points you win! You can score even if your horse doesn't win - just finishing in the top places earns points too.
                            </p>
                        </div>

                        {/* The Maths Explained Simply */}
                        <div className="p-3 bg-muted/50 rounded-lg">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-primary" />
                                How We Calculate Points
                            </h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                Each pick uses a <strong>£20 virtual stake</strong> split into two bets: £10 on your horse to <strong>WIN</strong>, and £10 on your horse to <strong>PLACE</strong> (finish in top positions).
                            </p>
                            <div className="bg-background/50 rounded p-3 space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <Trophy className="h-4 w-4 text-amber-400" />
                                    <span><strong>Win points</strong> = Odds × 10</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Medal className="h-4 w-4 text-blue-400" />
                                    <span><strong>Place points</strong> = Odds × 10 ÷ 4</span>
                                </div>
                                <p className="text-xs text-muted-foreground pt-2 border-t">
                                    "Odds" means the first number in the price (e.g., 4/1 = 4, 8/1 = 8, 20/1 = 20)
                                </p>
                            </div>
                        </div>
                        
                        {/* Points Calculation */}
                        <div>
                            <h4 className="font-semibold text-sm mb-2">📊 Points Breakdown</h4>
                            <div className="grid gap-3">
                                {/* Winner */}
                                <div className="p-3 bg-gradient-to-r from-amber-500/20 to-amber-500/5 rounded-lg border border-amber-500/30">
                                    <div className="flex items-center gap-3 mb-2">
                                        <Trophy className="h-6 w-6 text-amber-400" />
                                        <div>
                                            <p className="font-semibold">🏆 Winner (1st Place)</p>
                                            <p className="text-xs text-muted-foreground">You get BOTH win points AND place points!</p>
                                        </div>
                                    </div>
                                    <div className="bg-background/50 rounded p-2 space-y-2">
                                        <div className="flex justify-between items-center text-sm border-b pb-2">
                                            <span>Horse at <strong>4/1</strong> wins:</span>
                                            <div className="text-right">
                                                <span className="font-data text-green-400 font-bold">+50 pts</span>
                                                <p className="text-[10px] text-muted-foreground">(40 win + 10 place)</p>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-sm border-b pb-2">
                                            <span>Horse at <strong>8/1</strong> wins:</span>
                                            <div className="text-right">
                                                <span className="font-data text-green-400 font-bold">+100 pts</span>
                                                <p className="text-[10px] text-muted-foreground">(80 win + 20 place)</p>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span>Horse at <strong>20/1</strong> wins:</span>
                                            <div className="text-right">
                                                <span className="font-data text-green-400 font-bold">+250 pts</span>
                                                <p className="text-[10px] text-muted-foreground">(200 win + 50 place)</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Placed */}
                                <div className="p-3 bg-gradient-to-r from-blue-500/20 to-blue-500/5 rounded-lg border border-blue-500/30">
                                    <div className="flex items-center gap-3 mb-2">
                                        <Medal className="h-6 w-6 text-blue-400" />
                                        <div>
                                            <p className="font-semibold">🥈 Placed (2nd, 3rd, or 4th)</p>
                                            <p className="text-xs text-muted-foreground">You only get place points (Odds × 10 ÷ 4)</p>
                                        </div>
                                    </div>
                                    <div className="bg-background/50 rounded p-2 space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span>Horse at <strong>4/1</strong> places:</span>
                                            <span className="font-data text-blue-400 font-bold">+10 pts</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span>Horse at <strong>8/1</strong> places:</span>
                                            <span className="font-data text-blue-400 font-bold">+20 pts</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span>Horse at <strong>20/1</strong> places:</span>
                                            <span className="font-data text-blue-400 font-bold">+50 pts</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Lost */}
                                <div className="p-2 bg-muted/30 rounded-lg border border-muted">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">❌</span>
                                        <div>
                                            <p className="font-medium text-sm">Unplaced (outside paying positions)</p>
                                            <p className="text-xs text-muted-foreground">0 points - but no penalty!</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* SP Explanation */}
                        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                            <h4 className="font-semibold text-sm mb-2">💰 What Do The Odds Mean?</h4>
                            <p className="text-sm text-muted-foreground mb-2">
                                Odds like "4/1" or "20/1" show how likely a horse is to win. <strong>Higher odds = less likely to win = MORE points if they do!</strong>
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="p-2 bg-background rounded text-center">
                                    <span className="font-data font-bold text-lg">2/1</span>
                                    <p className="text-muted-foreground">Favourite</p>
                                    <p className="text-green-500 font-semibold">25 pts</p>
                                </div>
                                <div className="p-2 bg-background rounded text-center">
                                    <span className="font-data font-bold text-lg">8/1</span>
                                    <p className="text-muted-foreground">Mid-range</p>
                                    <p className="text-green-500 font-semibold">100 pts</p>
                                </div>
                                <div className="p-2 bg-background rounded text-center">
                                    <span className="font-data font-bold text-lg">25/1</span>
                                    <p className="text-muted-foreground">Outsider</p>
                                    <p className="text-green-500 font-semibold">312.5 pts</p>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 text-center italic">
                                Points shown are for a winner. Places pay 1/4 of win points.
                            </p>
                        </div>
                        
                        {/* Place Terms */}
                        <div>
                            <h4 className="font-semibold text-sm mb-2">🏁 How Many Places Are Paid?</h4>
                            <p className="text-xs text-muted-foreground mb-2">
                                The number of paying places depends on how many horses are running:
                            </p>
                            <div className="overflow-hidden rounded-lg border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                        <tr>
                                            <th className="text-left p-2 font-medium">Runners</th>
                                            <th className="text-left p-2 font-medium">Places Paid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-t">
                                            <td className="p-2">2-4 horses</td>
                                            <td className="p-2 text-muted-foreground">Win only (no places)</td>
                                        </tr>
                                        <tr className="border-t">
                                            <td className="p-2">5-7 horses</td>
                                            <td className="p-2">1st, 2nd</td>
                                        </tr>
                                        <tr className="border-t">
                                            <td className="p-2">8-15 horses</td>
                                            <td className="p-2">1st, 2nd, 3rd</td>
                                        </tr>
                                        <tr className="border-t bg-primary/5">
                                            <td className="p-2">16+ (handicaps)</td>
                                            <td className="p-2">1st, 2nd, 3rd, 4th</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        {/* Worked Example */}
                        <div className="p-3 bg-gradient-to-r from-primary/10 to-transparent rounded-lg border border-primary/20">
                            <h4 className="font-semibold text-sm mb-2">📝 Worked Example: You Pick an 8/1 Shot</h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                In a 12-runner race (3 places paid), here's what you'd earn:
                            </p>
                            <div className="space-y-2 text-sm">
                                <div className="p-2 bg-green-500/10 rounded border border-green-500/20">
                                    <div className="flex justify-between items-center">
                                        <span className="text-green-500 font-semibold">🏆 If it WINS:</span>
                                        <span className="font-data font-bold text-green-500">+100 pts</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Win: 8 × 10 = 80 pts | Place: 8 × 10 ÷ 4 = 20 pts | Total: 100 pts
                                    </p>
                                </div>
                                <div className="p-2 bg-blue-500/10 rounded border border-blue-500/20">
                                    <div className="flex justify-between items-center">
                                        <span className="text-blue-500 font-semibold">🥈 If it finishes 2nd or 3rd:</span>
                                        <span className="font-data font-bold text-blue-500">+20 pts</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        No win points, just place: 8 × 10 ÷ 4 = 20 pts
                                    </p>
                                </div>
                                <div className="p-2 bg-muted/30 rounded">
                                    <div className="flex justify-between items-center text-muted-foreground">
                                        <span>❌ If it finishes 4th or worse:</span>
                                        <span className="font-data font-bold">0 pts</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Non-Runner Rule */}
                        <div className="p-3 bg-gradient-to-r from-amber-500/20 to-amber-500/5 rounded-lg border border-amber-500/30">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <span className="text-lg">🔄</span>
                                What If My Pick Is a Non-Runner?
                            </h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                If your selected horse is withdrawn from the race (non-runner), you automatically get the <strong className="text-amber-500">race favorite</strong> as your substitute. Your points are calculated based on how the favorite finishes.
                            </p>
                            <div className="bg-background/50 rounded p-3 space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-amber-500">1.</span>
                                    <span>Your original pick becomes a <strong>non-runner</strong></span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-amber-500">2.</span>
                                    <span>System finds the <strong>favorite</strong> (lowest odds horse)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-amber-500">3.</span>
                                    <span>Your points come from <strong>how the favorite finishes</strong></span>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 italic">
                                💡 Tip: Watch for the red warning on your picks before the race - you can change them!
                            </p>
                        </div>
                        
                        {/* Tips */}
                        <div className="p-3 bg-muted/30 rounded-lg">
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Lightbulb className="h-4 w-4 text-amber-500" />
                                Quick Tips
                            </h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• <strong>Big odds = big rewards</strong> - A 20/1 winner earns 250 pts!</li>
                                <li>• <strong>Each-way is your friend</strong> - Even 2nd or 3rd place scores points</li>
                                <li>• <strong>More runners = more places</strong> - Big fields pay out to more positions</li>
                                <li>• <strong>Favourites pay less</strong> - Low odds like 2/1 only give 25 pts for a win</li>
                            </ul>
                        </div>
    </>
);

const HowScoringWorks = () => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Card className="mb-4 border-blue-500/30 bg-blue-500/5">
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-blue-500/10 transition-colors py-3">
                        <CardTitle className="flex items-center justify-between text-base">
                            <div className="flex items-center gap-2">
                                <HelpCircle className="h-5 w-5 text-blue-500" />
                                <span>How Scoring Works</span>
                                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/30">
                                    £20 Virtual Stake
                                </Badge>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </CardTitle>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                        <ScoringRulesContent />
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};

const ScoringRulesModal = ({ open, onOpenChange }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-blue-500" />
                    How Scoring Works
                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/30">
                        £20 Virtual Stake
                    </Badge>
                </DialogTitle>
                <DialogDescription>
                    Here's how points are earned — you can reopen this any time from the "How Scoring Works" card.
                </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
                <ScoringRulesContent />
            </div>
            <DialogFooter>
                <Button className="w-full btn-pill" onClick={() => onOpenChange(false)}>
                    Got it, let's go!
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);

// Form String Display Component
const FormString = ({ form }) => {
    if (!form) return <span className="text-muted-foreground">-</span>;
    
    return (
        <div className="flex items-center gap-0.5">
            {form.split('').slice(-6).map((char, idx) => {
                let bgClass = 'bg-muted text-muted-foreground';
                if (char === '1') bgClass = 'bg-green-500 text-white';
                else if (char === '2') bgClass = 'bg-blue-500 text-white';
                else if (char === '3') bgClass = 'bg-blue-400 text-white';
                else if (char === '0') bgClass = 'bg-red-500/80 text-white';
                else if (char === 'F' || char === 'U' || char === 'P') bgClass = 'bg-red-600 text-white';
                else if (char === '-') bgClass = 'bg-transparent text-muted-foreground border border-border';
                
                return (
                    <span 
                        key={idx} 
                        className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${bgClass}`}
                    >
                        {char}
                    </span>
                );
            })}
        </div>
    );
};

// Timeform Stars Component
const TimeformStars = ({ rating }) => {
    if (!rating && rating !== 0) return <span className="text-muted-foreground text-xs">-</span>;
    
    const numStars = parseInt(rating) || 0;
    const maxStars = 5;
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="flex items-center gap-0.5 cursor-help">
                        {[...Array(maxStars)].map((_, i) => (
                            <Star 
                                key={i} 
                                className={`h-3 w-3 ${i < numStars ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
                            />
                        ))}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="font-semibold">Timeform Rating: {numStars}/5</p>
                    <p className="text-xs text-muted-foreground">
                        {numStars >= 5 ? 'Top class performer' :
                         numStars >= 4 ? 'Very good horse' :
                         numStars >= 3 ? 'Above average' :
                         numStars >= 2 ? 'Average performer' :
                         'Below average'}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Experience Score Badge
const ExperienceScoreBadge = ({ runs, wins }) => {
    if (!runs || runs === 0) return <Badge variant="outline" className="text-xs">Debut</Badge>;
    
    const winRate = Math.round((wins / runs) * 100);
    let color = 'bg-slate-500';
    
    if (runs >= 20) {
        color = 'bg-purple-500';
    } else if (runs >= 10) {
        color = 'bg-blue-500';
    } else if (runs >= 5) {
        color = 'bg-emerald-500';
    }
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger>
                    <Badge className={`${color} text-white text-xs cursor-help`}>
                        {runs}R/{wins}W
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{runs} career runs, {wins} wins ({winRate}%)</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Enhanced Entrant Card Component - Matches RaceDetailPage racecard exactly
const EnhancedEntrantCard = ({ 
    entrant, 
    isPicked, 
    isExpertPick, 
    expertTipType, 
    isNonRunner, 
    aiPicks, 
    submittingPick, 
    onSelect,
    disabled = false,
    disabledReason = ''
}) => {
    const [showDetails, setShowDetails] = useState(false);
    
    // AI pick checks
    const isAiTopPick = aiPicks?.top_pick?.name === entrant.name;
    const isAiEachWay = aiPicks?.each_way?.name === entrant.name;
    const isAiDarkHorse = aiPicks?.dark_horse?.name === entrant.name;
    const hasAiPick = isAiTopPick || isAiEachWay || isAiDarkHorse;
    
    // Use flattened fields from backend API (with fallbacks to raw data)
    const commentary = entrant.commentary || 
        entrant.contentitems?.find(item => 
            item.profile === 'pinstickerlong' || item.profile === 'pinstickershort'
        )?.bodies?.en_GB;
    
    // Ratings - use flattened fields or extract from ratings array
    const timeformStar = entrant.timeform_star || 
        entrant.ratings?.find(r => r.code === 'entrant:rating:timeform:star')?.value;
    const adjustedRating = entrant.adjusted_rating || 
        entrant.ratings?.find(r => r.code === 'entrant:rating:adjusted')?.value;
    const orRating = entrant.or_rating || 
        entrant.ratings?.find(r => r.code === 'entrant:rating:official')?.value ||
        entrant.ratings?.find(r => r.code === 'entrant:rating:or')?.value || 
        entrant.official_rating || '-';
    
    // Weight - use flattened field
    const weight = entrant.weight || '-';
    
    // Career stats - use flattened fields
    const runs = entrant.career_runs || 0;
    const wins = entrant.career_wins || 0;
    
    // Course/Distance wins
    const courseWins = entrant.course_wins || 0;
    const distanceWins = entrant.distance_wins || 0;
    const courseDistanceWins = entrant.course_distance_wins || 0;
    
    // Silk description
    const silkDescription = entrant.silk_description || 
        entrant.contentitems?.find(i => i.profile === 'jockeycolours')?.bodies?.en_GB;
    
    // Check for any content/analysis to show
    const hasAnalysis = commentary || (hasAiPick && aiPicks?.overall_analysis);
    
    return (
        <TooltipProvider>
        <div 
            className={`runner-row p-3 sm:p-4 border-b last:border-b-0 rounded-lg mb-2 ${
                isNonRunner ? 'opacity-50 bg-muted/30' : 
                isPicked ? 'border-2 border-primary bg-primary/5 shadow-lg' : 
                'border hover:border-primary/50 hover:bg-muted/30'
            }`}
            data-testid={`runner-row-${entrant.id}`}
        >
            {/* Mobile: Stacked Layout */}
            <div className="flex items-start gap-2 sm:gap-3">
                {/* Jockey Silk */}
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="w-10 h-12 sm:w-12 sm:h-14 rounded overflow-hidden bg-muted flex-shrink-0">
                                {entrant.silk_url ? (
                                    <img 
                                        src={entrant.silk_url} 
                                        alt="Jockey silks"
                                        className={`w-full h-full object-contain ${isNonRunner ? 'opacity-40 grayscale' : ''}`}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-muted">
                                        <Users className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                                    </div>
                                )}
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs max-w-48">{silkDescription || 'Jockey silks'}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                {/* Runner Number */}
                <div className="flex flex-col items-center gap-1">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-data text-xs sm:text-sm font-bold ${
                        isPicked ? 'bg-primary text-primary-foreground' : 'bg-primary text-primary-foreground'
                    }`}>
                        {entrant.number || entrant.entrantnumber || '?'}
                    </div>
                </div>

                {/* Runner Info - Takes remaining space */}
                <div className="flex-1 min-w-0">
                    {/* Name Row */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <span className={`font-heading font-semibold text-base sm:text-lg leading-tight block ${isNonRunner ? 'line-through text-muted-foreground' : ''}`}>
                                {entrant.name}
                            </span>
                            {isNonRunner && <Badge variant="destructive" className="mt-1 text-xs">Non Runner</Badge>}
                        </div>
                        
                        {/* Odds - Fixed width, always visible */}
                        <div className="text-right flex-shrink-0 ml-2">
                            {!isNonRunner ? (
                                <div 
                                    className={`odds-btn px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-data font-bold text-base sm:text-lg whitespace-nowrap ${
                                        disabled 
                                            ? 'bg-muted text-muted-foreground' 
                                            : isPicked 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'bg-amber-400 text-black'
                                    }`}
                                >
                                    {entrant.odds_fractional || 'SP'}
                                </div>
                            ) : (
                                <span className="text-muted-foreground text-sm">N/R</span>
                            )}
                        </div>
                    </div>
                    
                    {/* Tip Badges - Wrap on mobile */}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {(runs > 0) && (
                            <ExperienceScoreBadge runs={runs} wins={wins} />
                        )}
                        {expertTipType === 'tip:pick:selection' && (
                            <Badge variant="outline" className="gap-1 bg-amber-500/20 text-amber-600 border-amber-500/30 text-xs">
                                <Star className="h-3 w-3" /> Podium Top Pick
                            </Badge>
                        )}
                        {expertTipType === 'tip:pick:alternative' && (
                            <Badge variant="outline" className="gap-1 bg-blue-500/20 text-blue-600 border-blue-500/30 text-xs">
                                <Target className="h-3 w-3" /> Each-Way
                            </Badge>
                        )}
                        {expertTipType === 'tip:pick:danger' && (
                            <Badge variant="outline" className="gap-1 bg-purple-500/20 text-purple-600 border-purple-500/30 text-xs">
                                <TrendingUp className="h-3 w-3" /> Dark Horse
                            </Badge>
                        )}
                        {isAiTopPick && (
                            <Badge variant="outline" className="gap-1 bg-rose-500/20 text-rose-600 border-rose-500/30 text-xs">
                                <Bot className="h-3 w-3" /> AI Top Pick
                            </Badge>
                        )}
                        {isAiEachWay && (
                            <Badge variant="outline" className="gap-1 bg-cyan-500/20 text-cyan-600 border-cyan-500/30 text-xs">
                                <Bot className="h-3 w-3" /> AI E/W
                            </Badge>
                        )}
                        {isAiDarkHorse && (
                            <Badge variant="outline" className="gap-1 bg-violet-500/20 text-violet-600 border-violet-500/30 text-xs">
                                <Bot className="h-3 w-3" /> AI Dark Horse
                            </Badge>
                        )}
                        {isPicked && <Check className="h-5 w-5 text-primary" />}
                    </div>

                    {/* Key Info Row - J/T/Wgt/OR - More compact on mobile */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs sm:text-sm mt-2">
                        <div className="flex items-center gap-1 truncate">
                            <span className="text-muted-foreground">J:</span>
                            <span className="font-medium truncate">{entrant.jockey || 'TBA'}</span>
                        </div>
                        <div className="flex items-center gap-1 truncate">
                            <span className="text-muted-foreground">T:</span>
                            <span className="font-medium truncate">{entrant.trainer || 'TBA'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">Wgt</span>
                            <span className="font-medium">{weight}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">OR</span>
                            <span className="font-data font-semibold">{orRating}</span>
                        </div>
                    </div>

                    {/* Ratings Row - Adj/TS */}
                    {(adjustedRating || timeformStar) && (
                        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm mt-1.5 flex-wrap">
                            {adjustedRating && (
                                <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Adj</span>
                                    <span className={`font-data font-semibold ${
                                        parseInt(adjustedRating) > parseInt(orRating) 
                                            ? 'text-emerald-500' 
                                            : parseInt(adjustedRating) < parseInt(orRating) 
                                                ? 'text-red-400' 
                                                : ''
                                    }`}>
                                        {adjustedRating}
                                        {orRating !== '-' && adjustedRating !== orRating && (
                                            <span className="text-xs ml-0.5">
                                                ({parseInt(adjustedRating) > parseInt(orRating) ? '+' : ''}{parseInt(adjustedRating) - parseInt(orRating)})
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {timeformStar && (
                                <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">TS</span>
                                    <TimeformStars rating={timeformStar} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Row */}
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap mt-1.5">
                        <div>
                            <span className="text-xs text-muted-foreground uppercase">Form</span>
                            <div className="mt-0.5">
                                <FormString form={entrant.form} />
                            </div>
                        </div>
                        
                        {/* Course/Distance Winner Badges */}
                        {(courseDistanceWins > 0 || courseWins > 0 || distanceWins > 0) && (
                            <div className="flex items-center gap-1 flex-wrap">
                                {courseDistanceWins > 0 && (
                                    <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30 text-xs">
                                        C&D ({courseDistanceWins})
                                    </Badge>
                                )}
                                {courseWins > 0 && courseDistanceWins === 0 && (
                                    <Badge variant="outline" className="text-xs">
                                        C ({courseWins})
                                    </Badge>
                                )}
                                {distanceWins > 0 && courseDistanceWins === 0 && (
                                    <Badge variant="outline" className="text-xs">
                                        D ({distanceWins})
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Expandable Analysis Toggle */}
                    {hasAnalysis && (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDetails(!showDetails);
                            }}
                            className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                        >
                            <Lightbulb className="h-3 w-3" />
                            {showDetails ? 'Hide analysis' : 'Show analysis'}
                        </button>
                    )}
                    
                    {/* Expanded Analysis Content */}
                    {showDetails && (
                        <div className="mt-3 space-y-3">
                            {/* Race Preview Commentary */}
                            {commentary && (
                                <div className="p-2 sm:p-3 bg-muted/50 rounded-lg border-l-2 border-primary/50">
                                    <div className="flex items-start gap-2">
                                        <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                            {commentary}
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {/* AI Analysis */}
                            {hasAiPick && aiPicks?.overall_analysis && (
                                <div className="p-2 sm:p-3 bg-purple-500/10 rounded-lg border-l-2 border-purple-500/50">
                                    <div className="flex items-start gap-2">
                                        <Bot className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-xs font-semibold text-purple-600 mb-1">AI Analysis</p>
                                            <p className="text-xs sm:text-sm leading-relaxed">{aiPicks.overall_analysis}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Individual AI pick reasoning */}
                            {isAiTopPick && aiPicks?.top_pick?.reasoning && (
                                <div className="p-2 bg-rose-500/10 rounded text-xs sm:text-sm">
                                    <span className="font-semibold text-rose-600">Why AI Top Pick:</span> {aiPicks.top_pick.reasoning}
                                </div>
                            )}
                            {isAiEachWay && aiPicks?.each_way?.reasoning && (
                                <div className="p-2 bg-cyan-500/10 rounded text-xs sm:text-sm">
                                    <span className="font-semibold text-cyan-600">Why AI E/W:</span> {aiPicks.each_way.reasoning}
                                </div>
                            )}
                            {isAiDarkHorse && aiPicks?.dark_horse?.reasoning && (
                                <div className="p-2 bg-violet-500/10 rounded text-xs sm:text-sm">
                                    <span className="font-semibold text-violet-600">Why AI Dark Horse:</span> {aiPicks.dark_horse.reasoning}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Select Button - Always visible, larger touch target */}
            <div className="mt-3 pt-3 border-t">
                {isNonRunner ? (
                    <div className="text-center text-sm text-muted-foreground py-2">
                        Non-runner - cannot be selected
                    </div>
                ) : disabled ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="w-full">
                                <Button 
                                    className="w-full h-12 text-base"
                                    variant="outline"
                                    disabled={true}
                                >
                                    <Lock className="h-4 w-4 mr-2" />
                                    Picks Locked
                                </Button>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{disabledReason || 'Picks not available yet'}</p>
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    <Button 
                        className={`w-full h-12 text-base ${isPicked ? 'bg-primary' : ''}`}
                        variant={isPicked ? "default" : "outline"}
                        disabled={submittingPick}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!submittingPick) onSelect(entrant);
                        }}
                    >
                        {submittingPick ? 'Submitting...' : isPicked ? '✓ Your Pick' : 'Select This Horse'}
                    </Button>
                )}
            </div>
        </div>
        </TooltipProvider>
    );
};

// Admin Daily Winners Management Panel
const AdminDailyWinnersPanel = ({ competitionId, leaderboard, competition }) => {
    const [dailyWinners, setDailyWinners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    
    // Get unique dates from competition races
    const raceDates = useMemo(() => {
        if (!competition?.races) return [];
        const dates = [...new Set(competition.races.map(r => r.race_date || r.race_time?.split('T')[0]))];
        return dates.filter(Boolean).sort();
    }, [competition?.races]);
    
    // Fetch daily winners
    const fetchDailyWinners = useCallback(async () => {
        try {
            const token = getToken();
            const response = await axios.get(
                `${API_URL}/api/admin/competitions/${competitionId}/daily-winners`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDailyWinners(response.data.daily_winners || []);
        } catch (err) {
            console.error('Failed to fetch daily winners:', err);
        } finally {
            setLoading(false);
        }
    }, [competitionId]);
    
    useEffect(() => {
        if (isOpen) {
            fetchDailyWinners();
        }
    }, [isOpen, fetchDailyWinners]);
    
    const handleAddWinner = async () => {
        if (!selectedDate || !selectedUser) {
            toast.error('Please select a date and user');
            return;
        }
        
        const user = leaderboard.find(e => e.user_id === selectedUser);
        if (!user) return;
        
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/admin/competitions/${competitionId}/daily-winners`,
                {
                    date: selectedDate,
                    user_id: user.user_id,
                    user_name: user.user_name,
                    points: user.daily_points?.[selectedDate] || 0
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Added ${user.user_name} as winner for ${selectedDate}`);
            setSelectedDate('');
            setSelectedUser('');
            fetchDailyWinners();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add winner');
        }
    };
    
    const handleRemoveWinner = async (date) => {
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/competitions/${competitionId}/daily-winners/${date}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Removed winner for ${date}`);
            fetchDailyWinners();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove winner');
        }
    };
    
    // Get players sorted by points for the selected date
    const sortedPlayersForDate = useMemo(() => {
        if (!selectedDate || !leaderboard) return [];
        
        return [...leaderboard]
            .map(entry => ({
                ...entry,
                pointsForDay: entry.daily_points?.[selectedDate] || 0
            }))
            .sort((a, b) => b.pointsForDay - a.pointsForDay);
    }, [leaderboard, selectedDate]);
    
    return (
        <Card className="mt-4 border-amber-500/30 bg-amber-500/5">
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-amber-500/10 transition-colors py-3">
                        <CardTitle className="flex items-center justify-between text-base">
                            <div className="flex items-center gap-2">
                                <Crown className="h-5 w-5 text-amber-500" />
                                <span>Daily Winners</span>
                                <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/30">
                                    Admin
                                </Badge>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </CardTitle>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                        {/* Current Daily Winners */}
                        <div>
                            <h4 className="text-sm font-semibold mb-2">Current Winners</h4>
                            {loading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-full" />
                                </div>
                            ) : dailyWinners.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No daily winners set</p>
                            ) : (
                                <div className="space-y-2">
                                    {dailyWinners.map(winner => (
                                        <div 
                                            key={winner.date}
                                            className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Medal className="h-4 w-4 text-amber-400" />
                                                <div>
                                                    <p className="text-sm font-medium">{winner.user_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {format(new Date(winner.date), 'EEE d MMM')} • {winner.points?.toFixed(1) || 0} pts
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                                onClick={() => handleRemoveWinner(winner.date)}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Add New Winner */}
                        <div className="pt-3 border-t">
                            <h4 className="text-sm font-semibold mb-2">Add Winner</h4>
                            <div className="space-y-2">
                                <select
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full p-2 rounded-lg bg-muted/50 border border-border text-sm"
                                >
                                    <option value="">Select date...</option>
                                    {raceDates.map(date => (
                                        <option key={date} value={date}>
                                            {format(new Date(date), 'EEE d MMM yyyy')}
                                            {dailyWinners.find(w => w.date === date) && ' (has winner)'}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="w-full p-2 rounded-lg bg-muted/50 border border-border text-sm"
                                >
                                    <option value="">Select player...</option>
                                    {sortedPlayersForDate.map(entry => (
                                        <option key={entry.user_id} value={entry.user_id}>
                                            {entry.user_name} ({entry.pointsForDay.toFixed(1)} pts)
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    onClick={handleAddWinner}
                                    disabled={!selectedDate || !selectedUser}
                                    className="w-full"
                                    size="sm"
                                >
                                    <Medal className="h-4 w-4 mr-2" />
                                    Add Daily Winner
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};

export default function CompetitionDetailPage() {
    const { competitionId } = useParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const justSignedUp = !!location.state?.justSignedUp;
    const [showRulesModal, setShowRulesModal] = useState(justSignedUp);

    const [competition, setCompetition] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [raceHeaders, setRaceHeaders] = useState([]);
    const [userEntry, setUserEntry] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [leaderboardDateFilter, setLeaderboardDateFilter] = useState(null); // null = overall
    const [loading, setLoading] = useState(true);
    const [approvedDailyWinners, setApprovedDailyWinners] = useState([]); // Only show winners approved by admin
    const [joining, setJoining] = useState(false);
    const [activeTab, setActiveTab] = useState('picks');
    const [selectedDay, setSelectedDay] = useState(1); // Day tabs state
    const [userSelectedDay, setUserSelectedDay] = useState(false); // Track if user manually selected a day
    const [selectedRace, setSelectedRace] = useState(null);
    const [raceEntrants, setRaceEntrants] = useState([]);
    const [expertTips, setExpertTips] = useState([]);
    const [aiPicks, setAiPicks] = useState(null); // AI picks for the race
    const [commentary, setCommentary] = useState(null);
    const [raceResults, setRaceResults] = useState(null); // Full results with commentary
    const [picksDistribution, setPicksDistribution] = useState(null);
    const [showOtherPicks, setShowOtherPicks] = useState(false);
    const [loadingEntrants, setLoadingEntrants] = useState(false);
    const [submittingPick, setSubmittingPick] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [expandedPlayer, setExpandedPlayer] = useState(null); // For expandable race form

    // Join competition function
    const handleJoinCompetition = async () => {
        if (!competition?.invite_code) {
            toast.error('Cannot join - no invite code available');
            return;
        }
        
        setJoining(true);
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/competitions/join/${competition.invite_code}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Successfully joined the competition!');
            // Refresh competition data
            await fetchCompetition();
        } catch (err) {
            console.error('Failed to join:', err);
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    };

    const fetchCompetition = useCallback(async () => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/competitions/' + competitionId);
            return;
        }
        
        try {
            const token = getToken();
            const dateParam = leaderboardDateFilter ? `&date=${leaderboardDateFilter}` : '';
            const [compRes, leaderboardRes, dailyWinnersRes] = await Promise.all([
                axios.get(`${API_URL}/api/competitions/${competitionId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/api/competitions/${competitionId}/leaderboard?include_form=true${dateParam}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                // Fetch approved daily winners - this endpoint is public for display
                axios.get(`${API_URL}/api/competitions/${competitionId}/approved-daily-winners`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: { daily_winners: [] } }))
            ]);
            
            setCompetition(compRes.data.competition);
            setLeaderboard(leaderboardRes.data.leaderboard || []);
            setRaceHeaders(leaderboardRes.data.race_headers || []);
            setUserEntry(leaderboardRes.data.user_entry);
            setAvailableDates(leaderboardRes.data.available_dates || []);
            setApprovedDailyWinners(dailyWinnersRes.data.daily_winners || []);
        } catch (err) {
            console.error('Failed to fetch competition:', err);
            toast.error('Failed to load competition');
            navigate('/competitions');
        } finally {
            setLoading(false);
        }
    }, [competitionId, isAuthenticated, navigate, leaderboardDateFilter]);

    // Refetch leaderboard when date filter changes
    const fetchLeaderboardOnly = useCallback(async () => {
        if (!isAuthenticated) return;
        
        try {
            const token = getToken();
            const dateParam = leaderboardDateFilter ? `&date=${leaderboardDateFilter}` : '';
            const leaderboardRes = await axios.get(
                `${API_URL}/api/competitions/${competitionId}/leaderboard?include_form=true${dateParam}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setLeaderboard(leaderboardRes.data.leaderboard || []);
            setRaceHeaders(leaderboardRes.data.race_headers || []);
            setUserEntry(leaderboardRes.data.user_entry);
        } catch (err) {
            console.error('Failed to fetch leaderboard:', err);
        }
    }, [competitionId, isAuthenticated, leaderboardDateFilter]);

    // Fetch leaderboard when date filter changes (after initial load)
    useEffect(() => {
        if (competition && leaderboardDateFilter !== undefined) {
            fetchLeaderboardOnly();
        }
    }, [leaderboardDateFilter, fetchLeaderboardOnly, competition]);

    useEffect(() => {
        fetchCompetition();
    }, [fetchCompetition]);

    // Auto-select current day (or first unfinished day) when competition loads
    // But only if user hasn't manually selected a day
    useEffect(() => {
        if (!competition?.races || competition.races.length === 0) return;
        if (userSelectedDay) return; // Don't override user's selection
        
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        
        // Get all unique days
        const daysSet = new Set();
        competition.races.forEach(race => {
            daysSet.add(race.day_number || 1);
        });
        const days = Array.from(daysSet).sort((a, b) => a - b);
        
        if (days.length <= 1) return; // No need to change if single day
        
        // Find the current day or first unfinished day
        let targetDay = days[days.length - 1]; // Default to last day
        
        for (const dayNum of days) {
            const dayRaces = competition.races.filter(r => (r.day_number || 1) === dayNum);
            const allFinished = dayRaces.every(r => r.status === 'finished');
            const dayDate = dayRaces[0]?.race_date;
            
            // If this day matches today's date, select it
            if (dayDate === today) {
                targetDay = dayNum;
                break;
            }
            
            // If this day is not fully finished, select it (first active day)
            if (!allFinished) {
                targetDay = dayNum;
                break;
            }
        }
        
        setSelectedDay(targetDay);
    }, [competition, userSelectedDay]);

    const fetchRaceEntrants = async (race) => {
        setSelectedRace(race);
        setLoadingEntrants(true);
        setExpertTips([]);
        setAiPicks(null);
        setCommentary(null);
        setRaceResults(null);
        setPicksDistribution(null);
        setShowOtherPicks(false);
        setRaceEntrants([]);
        
        // Check if race entries are declared
        // Declarations happen at 10am, 2 days before the race DATE
        const raceTime = new Date(race.race_time);
        const now = new Date();
        const declarationDate = new Date(raceTime);
        declarationDate.setDate(declarationDate.getDate() - 2);
        declarationDate.setHours(10, 0, 0, 0);
        const entriesNotDeclared = now < declarationDate;
        
        try {
            const token = getToken();
            
            // Fetch entrants, picks distribution, and AI picks in parallel
            const [entrantsRes, distributionRes, aiPicksRes] = await Promise.all([
                axios.get(
                    `${API_URL}/api/competitions/${competitionId}/races/${race.event_id}/entrants`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ),
                axios.get(
                    `${API_URL}/api/competitions/${competitionId}/races/${race.event_id}/picks-distribution`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ).catch(() => ({ data: { available: false } })),
                axios.get(
                    `${API_URL}/api/ai-picks/${race.event_id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ).catch(() => ({ data: null }))
            ]);
            
            setRaceEntrants(entrantsRes.data.entrants || []);
            setExpertTips(entrantsRes.data.expert_tips || []);
            setCommentary(entrantsRes.data.commentary || null);
            setRaceResults(entrantsRes.data.results || null);
            
            // AI picks API returns { available: true, data: { picks: {...} } }
            if (aiPicksRes.data?.available && aiPicksRes.data?.data?.picks) {
                setAiPicks(aiPicksRes.data.data.picks);
            } else {
                setAiPicks(null);
            }
            
            if (distributionRes.data.available) {
                setPicksDistribution(distributionRes.data);
            }
        } catch (err) {
            console.error('Failed to fetch entrants:', err);
            // Check if this is likely an undeclared race
            if (entriesNotDeclared) {
                // Don't show error toast - we'll handle this in the UI
                console.log('Race entries not yet declared');
            } else {
                toast.error('Failed to load race entries');
            }
        } finally {
            setLoadingEntrants(false);
        }
    };

    const submitPick = async (entrant) => {
        if (!selectedRace) return;
        
        setSubmittingPick(true);
        try {
            const token = getToken();
            const existingPick = competition.my_picks?.[selectedRace.event_id];
            
            // Allow change if: no change used yet OR pick is a non-runner (unlimited changes for non-runners)
            const canChangePick = existingPick && (!existingPick.change_used || existingPick.pick_is_non_runner);
            
            if (canChangePick) {
                // Change existing pick
                await axios.put(
                    `${API_URL}/api/competitions/${competitionId}/picks/${selectedRace.event_id}`,
                    {
                        event_id: selectedRace.event_id,
                        new_entrant_id: entrant.id,
                        new_entrant_name: entrant.name,
                        new_entrant_number: entrant.number ? String(entrant.number) : null,
                        new_silk_url: entrant.silk_url || null
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (existingPick.pick_is_non_runner) {
                    toast.success(`Replaced non-runner with ${entrant.name}`);
                } else {
                    toast.success(`Changed pick to ${entrant.name}`);
                }
            } else if (!existingPick) {
                // Submit new pick
                await axios.post(
                    `${API_URL}/api/competitions/${competitionId}/picks`,
                    {
                        picks: [{
                            event_id: selectedRace.event_id,
                            entrant_id: entrant.id,
                            entrant_name: entrant.name,
                            entrant_number: String(entrant.number || ''),
                            silk_url: entrant.silk_url || ''
                        }]
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success(`Picked ${entrant.name}!`);
            } else {
                toast.error('You have already used your pick change for this race');
                return;
            }
            
            // Refresh competition data
            await fetchCompetition();
            setSelectedRace(null);
        } catch (err) {
            console.error('Failed to submit pick:', err);
            toast.error(err.response?.data?.detail || 'Failed to submit pick');
        } finally {
            setSubmittingPick(false);
        }
    };

    const inviteLinkPath = competition?.is_festival
        ? `/festival/join/${competition.invite_code}`
        : `/competitions/join/${competition?.invite_code}`;

    const copyInviteLink = () => {
        navigator.clipboard.writeText(`${window.location.origin}${inviteLinkPath}`);
        toast.success('Invite link copied to clipboard!');
    };

    const isDeadlinePassed = (race) => {
        const raceTime = new Date(race.race_time);
        const deadline = new Date(raceTime.getTime() - (competition.deadline_minutes * 60000));
        return new Date() > deadline;
    };

    const isRaceStarted = (race) => {
        const raceTime = new Date(race.race_time);
        return new Date() > raceTime;
    };

    const getRaceStatus = (race) => {
        if (race.status === 'finished') return { label: 'Finished', class: 'bg-purple-500/20 text-purple-400' };
        if (isRaceStarted(race)) return { label: 'Result Pending', class: 'bg-amber-500/20 text-amber-400' };
        if (isDeadlinePassed(race)) return { label: 'Locked', class: 'bg-red-500/20 text-red-400' };
        return { label: 'Open', class: 'bg-green-500/20 text-green-400' };
    };

    const groupRacesByDate = () => {
        if (!competition?.races) return {};
        return competition.races.reduce((groups, race) => {
            const date = race.race_date;
            if (!groups[date]) groups[date] = [];
            groups[date].push(race);
            return groups;
        }, {});
    };

    // Get unique days from competition with finished status and daily winner
    const getCompetitionDays = () => {
        if (!competition?.races) return [];
        
        const now = new Date();
        
        // Helper function to get approved daily winner for a date
        const getApprovedWinner = (date) => {
            const approved = approvedDailyWinners.find(w => w.date === date && w.approved);
            if (approved) {
                return { name: approved.user_name, points: approved.points };
            }
            return null;
        };
        
        // If competition has explicit days array, use it but enhance with status
        if (competition.days && competition.days.length > 0) {
            return competition.days.map(day => {
                const dayRaces = competition.races.filter(r => (r.day_number || 1) === day.day_number);
                const allFinished = dayRaces.length > 0 && dayRaces.every(r => r.status === 'finished');
                const hasStarted = dayRaces.some(r => new Date(r.race_time) <= now);
                
                // Only show daily winner if approved by admin
                const dayDate = dayRaces[0]?.race_date;
                const dailyWinner = dayDate ? getApprovedWinner(dayDate) : null;
                
                return {
                    ...day,
                    is_finished: allFinished,
                    has_started: hasStarted,
                    daily_winner: dailyWinner
                };
            }).sort((a, b) => a.day_number - b.day_number);
        }
        
        // Otherwise, derive days from races
        const daysMap = new Map();
        competition.races.forEach(race => {
            const dayNum = race.day_number || 1;
            if (!daysMap.has(dayNum)) {
                daysMap.set(dayNum, {
                    day_number: dayNum,
                    date: race.race_date,
                    meeting_name: race.stage_name,
                    race_count: 0,
                    finished_count: 0,
                    races: []
                });
            }
            const dayData = daysMap.get(dayNum);
            dayData.race_count++;
            dayData.races.push(race);
            if (race.status === 'finished') dayData.finished_count++;
        });
        
        return Array.from(daysMap.values()).map(day => {
            const allFinished = day.finished_count === day.race_count && day.race_count > 0;
            const hasStarted = day.races.some(r => new Date(r.race_time) <= now);
            
            // Only show daily winner if approved by admin
            const dailyWinner = day.date ? getApprovedWinner(day.date) : null;
            
            return {
                day_number: day.day_number,
                date: day.date,
                meeting_name: day.meeting_name,
                race_count: day.race_count,
                is_finished: allFinished,
                has_started: hasStarted,
                daily_winner: dailyWinner
            };
        }).sort((a, b) => a.day_number - b.day_number);
    };

    // Filter races by selected day
    const getRacesForDay = (dayNumber) => {
        if (!competition?.races) return [];
        return competition.races.filter(race => (race.day_number || 1) === dayNumber);
    };

    // Group races by date for the selected day
    const groupRacesByDateForDay = (dayNumber) => {
        const dayRaces = getRacesForDay(dayNumber);
        return dayRaces.reduce((groups, race) => {
            const date = race.race_date;
            if (!groups[date]) groups[date] = [];
            groups[date].push(race);
            return groups;
        }, {});
    };

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-4" />
                <Skeleton className="h-6 w-1/4 mb-8" />
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <Skeleton className="h-96" />
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    if (!competition) return null;

    const racesByDate = groupRacesByDate();
    const competitionDays = getCompetitionDays();
    const hasMultipleDays = competitionDays.length > 1;
    const racesByDateForSelectedDay = hasMultipleDays ? groupRacesByDateForDay(selectedDay) : racesByDate;

    // Calculate next upcoming race and check if all races are finished
    const now = new Date();
    const sortedRaces = [...(competition.races || [])].sort((a, b) => new Date(a.race_time) - new Date(b.race_time));
    const upcomingRaces = sortedRaces.filter(r => new Date(r.race_time) > now && r.status !== 'finished');
    const finishedRaces = sortedRaces.filter(r => r.status === 'finished');
    const nextRace = upcomingRaces[0];
    const allRacesFinished = finishedRaces.length === sortedRaces.length && sortedRaces.length > 0;

    return (
        <div className="container-app py-8">
            {/* Next Race Timer - Show at top when there are upcoming races */}
            {nextRace && competition.is_joined && (
                <Card className="mb-6 border-primary/30 bg-gradient-to-r from-primary/10 via-transparent to-primary/5" data-testid="next-race-card">
                    <CardContent className="py-4">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-primary/20 rounded-full">
                                    <Timer className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-semibold text-primary uppercase tracking-wide">Next Race</span>
                                        <Badge variant="outline" className="text-xs">
                                            {format(parseISO(nextRace.race_time), 'HH:mm')}
                                        </Badge>
                                    </div>
                                    <h3 className="font-semibold">{nextRace.event_title}</h3>
                                    <p className="text-sm text-muted-foreground">{nextRace.stage_name}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <CountdownTimer targetTime={nextRace.race_time} />
                                <Button 
                                    variant="default" 
                                    size="sm"
                                    onClick={() => {
                                        setSelectedRace(nextRace);
                                        fetchRaceEntrants(nextRace);
                                    }}
                                    data-testid="view-next-race-btn"
                                >
                                    {competition.my_picks?.[nextRace.event_id] ? 'View Pick' : 'Make Pick'}
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All Races Finished Banner */}
            {allRacesFinished && leaderboard.length > 0 && (
                <WinnerCelebration 
                    winner={leaderboard[0]} 
                    leaderboard={leaderboard}
                    totalRaces={finishedRaces.length}
                    currentUser={user}
                />
            )}

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-heading font-bold page-title">{competition.name}</h1>
                        {competition.is_featured && (
                            <Badge className="bg-amber-500/20 text-amber-400">
                                <Crown className="h-3 w-3 mr-1" />
                                Featured
                            </Badge>
                        )}
                    </div>
                    <p className="text-muted-foreground">{competition.meeting_name}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        {new Date(competition.start_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} - {new Date(competition.end_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowShareDialog(true)} data-testid="share-btn">
                        <Share2 className="h-4 w-4 mr-2" />
                        Invite
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchCompetition} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Join Competition Banner - for users who haven't joined */}
            {!competition.is_joined && (
                <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent">
                    <CardContent className="py-6">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-amber-500/20 rounded-full">
                                    <Trophy className="h-8 w-8 text-amber-500" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg">Join this competition!</h3>
                                    <p className="text-muted-foreground text-sm">
                                        Join now to make your picks and compete on the leaderboard.
                                    </p>
                                </div>
                            </div>
                            <Button 
                                onClick={handleJoinCompetition}
                                disabled={joining}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
                                data-testid="join-competition-btn"
                            >
                                {joining ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        Joining...
                                    </>
                                ) : (
                                    <>
                                        <Users className="h-4 w-4 mr-2" />
                                        Join Competition
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* User Stats Banner */}
            {userEntry && (
                <Card className="mb-6 bg-gradient-to-r from-primary/10 to-transparent border-primary/20">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground">Your Rank</p>
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold font-data">#{userEntry.rank}</span>
                                        {userEntry.rank_change > 0 && (
                                            <span className="text-green-400 flex items-center text-sm">
                                                <TrendingUp className="h-4 w-4" />+{userEntry.rank_change}
                                            </span>
                                        )}
                                        {userEntry.rank_change < 0 && (
                                            <span className="text-red-400 flex items-center text-sm">
                                                <TrendingDown className="h-4 w-4" />{userEntry.rank_change}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="h-10 w-px bg-border" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Points</p>
                                    <span className="text-2xl font-bold font-data">{userEntry.total_points.toFixed(1)}</span>
                                </div>
                                <div className="h-10 w-px bg-border" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Winners</p>
                                    <span className="text-2xl font-bold font-data text-green-400">{userEntry.correct_picks}</span>
                                </div>
                                <div className="h-10 w-px bg-border hidden sm:block" />
                                <div className="hidden sm:block">
                                    <p className="text-sm text-muted-foreground">Places</p>
                                    <span className="text-2xl font-bold font-data text-blue-400">{userEntry.placed_picks || 0}</span>
                                </div>
                            </div>
                            {userEntry.daily_winner_badges?.length > 0 && (
                                <div className="flex items-center gap-2">
                                    {userEntry.daily_winner_badges.map(date => (
                                        <Badge key={date} className="bg-amber-500/20 text-amber-400">
                                            <Medal className="h-3 w-3 mr-1" />
                                            Day Winner
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2">
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="mb-4">
                            <TabsTrigger value="picks" data-testid="picks-tab">Make Picks</TabsTrigger>
                            <TabsTrigger value="my-picks" data-testid="my-picks-tab">My Picks</TabsTrigger>
                            <TabsTrigger value="leaderboard" data-testid="leaderboard-tab" className="flex items-center gap-1">
                                <BarChart3 className="h-4 w-4" />
                                Leaderboard
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="picks">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Trophy className="h-5 w-5 text-primary" />
                                        Select Your Picks
                                    </CardTitle>
                                    <CardDescription>
                                        Pick one horse per race. You can change each pick once before the deadline.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {/* Day Tabs for Multi-Day Competitions */}
                                    {hasMultipleDays && (
                                        <div className="mb-6">
                                            <div className="flex flex-wrap gap-2 p-1 bg-muted/50 rounded-lg">
                                                {competitionDays.map((day) => (
                                                    <Button
                                                        key={day.day_number}
                                                        variant={selectedDay === day.day_number ? "default" : "ghost"}
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedDay(day.day_number);
                                                            setUserSelectedDay(true);
                                                        }}
                                                        className={`flex-1 min-w-[100px] relative ${selectedDay === day.day_number ? '' : 'hover:bg-muted'} ${day.is_finished ? 'opacity-80' : ''}`}
                                                        data-testid={`day-tab-${day.day_number}`}
                                                    >
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <div className="flex items-center gap-1">
                                                                <span className="font-semibold">Day {day.day_number}</span>
                                                                {day.is_finished && (
                                                                    <CheckCircle className="h-3 w-3 text-green-500" />
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] opacity-70">
                                                                {day.meeting_name}
                                                            </span>
                                                        </div>
                                                    </Button>
                                                ))}
                                            </div>
                                            {/* Day info with winner */}
                                            {(() => {
                                                const currentDay = competitionDays.find(d => d.day_number === selectedDay);
                                                return (
                                                    <div className="text-center mt-3">
                                                        <p className="text-xs text-muted-foreground">
                                                            {currentDay?.race_count || 0} races on this day
                                                            {currentDay?.is_finished && ' • Completed'}
                                                        </p>
                                                        {currentDay?.is_finished && currentDay?.daily_winner && (
                                                            <div className="mt-2 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1">
                                                                <Medal className="h-4 w-4 text-amber-500" />
                                                                <span className="text-sm">
                                                                    <span className="text-amber-500 font-semibold">{currentDay.daily_winner.name}</span>
                                                                    <span className="text-muted-foreground"> won the day with </span>
                                                                    <span className="text-green-500 font-semibold">{currentDay.daily_winner.points.toFixed(1)} pts</span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                    
                                    {Object.entries(racesByDateForSelectedDay).map(([date, races]) => (
                                        <div key={date} className="mb-6 last:mb-0">
                                            <h3 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
                                                {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                                            </h3>
                                            <div className="space-y-3">
                                                {races.map(race => {
                                                    const status = getRaceStatus(race);
                                                    const myPick = competition.my_picks?.[race.event_id];
                                                    const canPick = !isDeadlinePassed(race) && race.status !== 'finished';
                                                    const isFinished = race.status === 'finished';
                                                    const raceStarted = isRaceStarted(race);
                                                    const canViewCard = !canPick; // Can view card when locked or finished
                                                    const canInteract = competition.is_joined; // Only joined users can interact
                                                    
                                                    // Check if entries are declared
                                                    // Declarations happen at 10am, 2 days before the race DATE (not race time)
                                                    const raceTime = new Date(race.race_time);
                                                    const now = new Date();
                                                    
                                                    // Calculate declaration datetime: 10am, 2 days before race date
                                                    const declarationDate = new Date(raceTime);
                                                    declarationDate.setDate(declarationDate.getDate() - 2);
                                                    declarationDate.setHours(10, 0, 0, 0);
                                                    
                                                    // Entries are NOT declared if we're before the declaration time
                                                    const entriesNotDeclared = now < declarationDate;
                                                    
                                                    return (
                                                        <div 
                                                            key={race.event_id}
                                                            className={`rounded-lg border transition-colors overflow-hidden ${
                                                                canInteract ? 'hover:border-primary/50 cursor-pointer' : 'opacity-75'
                                                            } ${selectedRace?.event_id === race.event_id ? 'border-primary bg-primary/5' : ''} ${entriesNotDeclared ? 'border-amber-500/30' : ''}`}
                                                            data-testid={`race-${race.event_id}`}
                                                            onClick={() => canInteract ? fetchRaceEntrants(race) : toast.error('Please join the competition first to view race details')}
                                                        >
                                                            {/* Race Header */}
                                                            <div 
                                                                className={`p-4 ${isFinished || raceStarted ? 'bg-muted/30' : ''}`}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className="font-data text-sm">
                                                                                {new Date(race.race_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                                            </span>
                                                                            {entriesNotDeclared ? (
                                                                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                                                                    <Clock className="h-3 w-3 mr-1" />
                                                                                    Entries Coming
                                                                                </Badge>
                                                                            ) : (
                                                                                <Badge className={status.class}>{status.label}</Badge>
                                                                            )}
                                                                            {!entriesNotDeclared && race.num_runners && (
                                                                                <span className="text-xs text-muted-foreground">
                                                                                    {race.num_runners} runners
                                                                                </span>
                                                                            )}
                                                                            {!canInteract && (
                                                                                <Badge variant="outline" className="text-xs">
                                                                                    Join to view
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        <p className="font-medium">{race.event_title}</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        {entriesNotDeclared ? (
                                                                            <div className="text-right">
                                                                                <p className="text-xs text-amber-400">Declared</p>
                                                                                <p className="text-sm text-muted-foreground">
                                                                                    {(() => {
                                                                                        const declDate = new Date(raceTime);
                                                                                        declDate.setDate(declDate.getDate() - 2);
                                                                                        return declDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                                                                                    })()}
                                                                                </p>
                                                                            </div>
                                                                        ) : canInteract && myPick ? (
                                                                            <div className={`flex items-center gap-2 ${myPick.pick_is_non_runner && !isFinished ? (canPick ? 'bg-red-500/20 border border-red-500' : 'bg-amber-500/20 border border-amber-500') + ' rounded-lg px-3 py-2 -my-1' : ''}`}>
                                                                                {/* Non-runner substitution display */}
                                                                                {myPick.was_non_runner ? (
                                                                                    (() => {
                                                                                        // Find substitute from results if not stored
                                                                                        let substituteName = myPick.substitute_name;
                                                                                        let substituteSilk = myPick.substitute_silk_url;
                                                                                        let substituteNumber = myPick.substitute_number;
                                                                                        
                                                                                        // If substitute not stored, find lowest SP horse from results (that's the favorite)
                                                                                        if (!substituteName && race.results && race.results.length > 0) {
                                                                                            const lowestSpResult = race.results.reduce((lowest, curr) => {
                                                                                                const currSp = curr.sp_decimal || 999;
                                                                                                const lowestSp = lowest.sp_decimal || 999;
                                                                                                return currSp < lowestSp ? curr : lowest;
                                                                                            }, race.results[0]);
                                                                                            substituteName = lowestSpResult.entrant_name;
                                                                                            substituteSilk = lowestSpResult.silk_url;
                                                                                            substituteNumber = lowestSpResult.entrant_number;
                                                                                        }
                                                                                        
                                                                                        return (
                                                                                            <div className="flex items-center gap-2">
                                                                                                {/* Original pick (crossed out) */}
                                                                                                <div className="flex flex-col items-center opacity-50">
                                                                                                    {myPick.silk_url ? (
                                                                                                        <img 
                                                                                                            src={myPick.silk_url} 
                                                                                                            alt="Original pick silks" 
                                                                                                            className="w-6 h-6 object-contain rounded grayscale"
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <div className="w-6 h-6 rounded bg-muted flex items-center justify-center text-[10px] font-bold">
                                                                                                            {myPick.entrant_number || '?'}
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <span className="text-[9px] text-muted-foreground line-through">{myPick.entrant_name?.split(' ')[0]}</span>
                                                                                                </div>
                                                                                                {/* Arrow */}
                                                                                                <span className="text-amber-500 text-lg">→</span>
                                                                                                {/* Substitute (favorite) */}
                                                                                                <div className="flex flex-col items-center">
                                                                                                    {substituteSilk ? (
                                                                                                        <img 
                                                                                                            src={substituteSilk} 
                                                                                                            alt="Substitute silks" 
                                                                                                            className="w-8 h-8 object-contain rounded"
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <div className="w-8 h-8 rounded bg-amber-500/20 flex items-center justify-center text-amber-500 text-xs font-bold">
                                                                                                            #{substituteNumber || 'F'}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                                <div className="text-right">
                                                                                                    <p className="text-[10px] text-amber-500">Non-Runner → Fav</p>
                                                                                                    <p className="font-semibold text-amber-500">{substituteName || 'Favorite'}</p>
                                                                                                    {isFinished && myPick.points_earned > 0 && (
                                                                                                        <p className="text-xs text-green-500 font-semibold">+{myPick.points_earned.toFixed(1)} pts</p>
                                                                                                    )}
                                                                                                    {isFinished && myPick.points_earned === 0 && (
                                                                                                        <p className="text-xs text-muted-foreground">0 pts</p>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    })()
                                                                                ) : (
                                                                                    /* Normal pick display */
                                                                                    <>
                                                                                        {/* Silk and number */}
                                                                                        {myPick.silk_url ? (
                                                                                            <img 
                                                                                                src={myPick.silk_url} 
                                                                                                alt="Jockey silks" 
                                                                                                className="w-8 h-8 object-contain rounded"
                                                                                            />
                                                                                        ) : myPick.entrant_number ? (
                                                                                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-sm font-bold">
                                                                                                {myPick.entrant_number}
                                                                                            </div>
                                                                                        ) : null}
                                                                                        <div className="text-right">
                                                                                            <p className="text-xs text-muted-foreground">Your Pick</p>
                                                                                            <p className={`font-semibold ${myPick.pick_is_non_runner && !isFinished ? 'text-red-500 line-through' : 'text-primary'}`}>
                                                                                                {myPick.entrant_name}
                                                                                            </p>
                                                                                            {myPick.pick_is_non_runner && !isFinished && canPick && (
                                                                                                <p className="text-xs text-red-500 font-semibold">⚠️ NON-RUNNER - Change pick!</p>
                                                                                            )}
                                                                                            {myPick.pick_is_non_runner && !isFinished && !canPick && (
                                                                                                <p className="text-xs text-amber-400 font-semibold">
                                                                                                    ⚠️ NON-RUNNER → {myPick.substitute_favorite_name || race.favorite_name || 'Favourite'}
                                                                                                </p>
                                                                                            )}
                                                                                            {myPick.change_used && !isFinished && !myPick.pick_is_non_runner && (
                                                                                                <p className="text-xs text-amber-400">Change used</p>
                                                                                            )}
                                                                                            {isFinished && myPick.points_earned > 0 && (
                                                                                                <p className="text-xs text-green-500 font-semibold">+{myPick.points_earned.toFixed(1)} pts</p>
                                                                                            )}
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        ) : canInteract && canPick ? (
                                                                            <span className="text-sm text-muted-foreground">No pick yet</span>
                                                                        ) : canInteract ? (
                                                                            <span className="text-sm text-red-400">Missed</span>
                                                                        ) : null}
                                                                        {canInteract && !entriesNotDeclared && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Show top 3 results inline for finished races */}
                                                                {isFinished && race.results && race.results.length > 0 && (
                                                                    <div className="mt-3 pt-3 border-t border-border/50">
                                                                        <div className="flex items-center gap-1 mb-2">
                                                                            <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                                                                                Result
                                                                            </Badge>
                                                                        </div>
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            {race.results.slice(0, 3).map((result, idx) => {
                                                                                const positionColors = [
                                                                                    'bg-amber-500 text-black',      // 1st - Gold
                                                                                    'bg-gray-400 text-black',       // 2nd - Silver
                                                                                    'bg-amber-700 text-white'       // 3rd - Bronze
                                                                                ];
                                                                                const positionLabels = ['1st', '2nd', '3rd'];
                                                                                const myPick = competition.my_picks?.[race.event_id];
                                                                                const isMyPick = myPick?.entrant_id === result.entrant_id;
                                                                                
                                                                                return (
                                                                                    <div 
                                                                                        key={result.entrant_id}
                                                                                        className={`p-2 rounded-lg ${idx === 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/30'} ${isMyPick ? 'ring-1 ring-primary' : ''}`}
                                                                                    >
                                                                                        <div className="flex items-center gap-1.5 mb-1">
                                                                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${positionColors[idx]}`}>
                                                                                                {idx + 1}
                                                                                            </span>
                                                                                            <span className="text-[10px] text-muted-foreground">{positionLabels[idx]}</span>
                                                                                            {idx === 0 && <Trophy className="h-3 w-3 text-amber-500" />}
                                                                                        </div>
                                                                                        {/* Silk and horse number */}
                                                                                        <div className="flex items-center gap-1.5 mb-1">
                                                                                            {result.silk_url ? (
                                                                                                <img 
                                                                                                    src={result.silk_url} 
                                                                                                    alt="Jockey silks" 
                                                                                                    className="w-6 h-6 object-contain rounded"
                                                                                                />
                                                                                            ) : (
                                                                                                <div className="w-6 h-6 rounded bg-muted flex items-center justify-center text-[10px] font-bold">
                                                                                                    {result.entrant_number || '?'}
                                                                                                </div>
                                                                                            )}
                                                                                            {result.silk_url && result.entrant_number && (
                                                                                                <span className="text-[10px] font-bold text-muted-foreground">
                                                                                                    #{result.entrant_number}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <p className="font-medium text-xs truncate" title={result.entrant_name}>
                                                                                            {result.entrant_name}
                                                                                        </p>
                                                                                        {/* Show points earned - use backend-calculated value */}
                                                                                        {result.points_earned > 0 && (
                                                                                            <p className={`text-[10px] font-semibold font-data ${result.position === 1 ? 'text-amber-500' : 'text-green-500'}`}>
                                                                                                +{result.points_earned.toFixed(1)} pts
                                                                                            </p>
                                                                                        )}
                                                                                        {isMyPick && (
                                                                                            <Badge className="bg-primary/20 text-primary text-[9px] mt-1 px-1 py-0">
                                                                                                Your Pick
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        {canInteract && (
                                                                            <p className="text-[10px] text-muted-foreground text-center mt-2">
                                                                                Click for full results →
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Fallback: Show only winner if no detailed results */}
                                                                {isFinished && race.winning_entrant_name && (!race.results || race.results.length === 0) && (
                                                                    <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <Trophy className="h-4 w-4 text-amber-500" />
                                                                            <span className="text-sm font-semibold">{race.winning_entrant_name}</span>
                                                                            {race.winning_sp && (
                                                                                <span className="text-green-400 font-data text-xs">
                                                                                    +{((race.winning_sp - 1) * 10 + (race.winning_sp - 1) * 10 / 4).toFixed(1)} pts
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {canInteract && (
                                                                            <Badge variant="outline" className="text-xs">
                                                                                View Full Results →
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Show pending status for started but not finished */}
                                                                {!isFinished && raceStarted && canInteract && (
                                                                    <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between">
                                                                        <span className="text-xs text-amber-500">Race started - awaiting result</span>
                                                                        <Badge variant="outline" className="text-xs">
                                                                            View Runners →
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="my-picks">
                            <Card>
                                <CardHeader>
                                    <CardTitle>My Picks & Results</CardTitle>
                                    <CardDescription>
                                        View your each-way picks and how they performed
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {/* Day Tabs for Multi-Day Competitions */}
                                    {hasMultipleDays && (
                                        <div className="mb-4">
                                            <div className="flex flex-wrap gap-2 p-1 bg-muted/50 rounded-lg">
                                                {competitionDays.map((day) => (
                                                    <Button
                                                        key={day.day_number}
                                                        variant={selectedDay === day.day_number ? "default" : "ghost"}
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedDay(day.day_number);
                                                            setUserSelectedDay(true);
                                                        }}
                                                        className={`flex-1 min-w-[100px] ${selectedDay === day.day_number ? '' : 'hover:bg-muted'} ${day.is_finished ? 'opacity-80' : ''}`}
                                                    >
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <div className="flex items-center gap-1">
                                                                <span className="font-semibold">Day {day.day_number}</span>
                                                                {day.is_finished && (
                                                                    <CheckCircle className="h-3 w-3 text-green-500" />
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] opacity-70">{day.meeting_name}</span>
                                                        </div>
                                                    </Button>
                                                ))}
                                            </div>
                                            {/* Daily winner display */}
                                            {(() => {
                                                const currentDay = competitionDays.find(d => d.day_number === selectedDay);
                                                if (currentDay?.is_finished && currentDay?.daily_winner) {
                                                    return (
                                                        <div className="text-center mt-2">
                                                            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 text-sm">
                                                                <Medal className="h-3 w-3 text-amber-500" />
                                                                <span className="text-amber-500 font-semibold">{currentDay.daily_winner.name}</span>
                                                                <span className="text-green-500 font-semibold">{currentDay.daily_winner.points.toFixed(1)} pts</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}
                                    
                                    <div className="space-y-3">
                                        {Object.entries(racesByDateForSelectedDay).map(([date, races]) => (
                                            <div key={date} className="mb-4">
                                                <h3 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">
                                                    {new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </h3>
                                                {races.map(race => {
                                                    const myPick = competition.my_picks?.[race.event_id];
                                                    const isWinner = myPick?.result_position === 1;
                                                    const isPlaced = myPick?.is_placed && !isWinner;
                                                    
                                                    return (
                                                        <div 
                                                            key={race.event_id} 
                                                            className={`p-3 rounded-lg mb-2 overflow-hidden ${
                                                                isWinner ? 'bg-green-500/10 border border-green-500/30' :
                                                                isPlaced ? 'bg-blue-500/10 border border-blue-500/30' :
                                                                myPick?.pick_is_non_runner && race.status !== 'finished' ? 'bg-amber-500/10 border border-amber-500/30' :
                                                                'bg-muted/30'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                {/* Silk and Number */}
                                                                {myPick && (
                                                                    <div className="relative shrink-0">
                                                                        {myPick.silk_url ? (
                                                                            <img 
                                                                                src={myPick.silk_url} 
                                                                                alt="Jockey silks" 
                                                                                className="w-10 h-10 object-contain"
                                                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                            />
                                                                        ) : null}
                                                                        <div 
                                                                            className={`w-10 h-10 rounded-lg items-center justify-center text-lg font-bold bg-muted ${myPick.silk_url ? 'hidden' : 'flex'}`}
                                                                        >
                                                                            {myPick.entrant_number || '?'}
                                                                        </div>
                                                                        {myPick.silk_url && myPick.entrant_number && (
                                                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-background border flex items-center justify-center text-xs font-bold">
                                                                                {myPick.entrant_number}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <div className="flex-1 min-w-0 overflow-hidden">
                                                                    <p className="text-sm text-muted-foreground truncate max-w-full">{race.event_title}</p>
                                                                    {myPick ? (
                                                                        <div className="overflow-hidden">
                                                                            <p className={`font-semibold truncate ${myPick.pick_is_non_runner && race.status !== 'finished' ? 'text-red-400 line-through' : ''}`}>
                                                                                {myPick.entrant_name}
                                                                                {myPick.was_non_runner && (
                                                                                    <span className="text-amber-400 text-sm ml-2">(NR → {myPick.substitute_name || 'Fav'})</span>
                                                                                )}
                                                                            </p>
                                                                            {/* Show substitute favourite for non-runner picks before race finishes */}
                                                                            {myPick.pick_is_non_runner && race.status !== 'finished' && (
                                                                                <p className="text-amber-400 text-sm font-semibold truncate">
                                                                                    → {myPick.substitute_favorite_name || race.favorite_name || 'Favourite'}
                                                                                </p>
                                                                            )}
                                                                            {myPick.picked_sp && (
                                                                                <p className="text-xs text-muted-foreground">
                                                                                    SP: {decimalToFractional(myPick.picked_sp)} • E/W {race.places_paid || 3} places
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-muted-foreground">No pick</p>
                                                                    )}
                                                                </div>
                                                                <div className="text-right shrink-0 ml-2">
                                                                    {isWinner ? (
                                                                        <div>
                                                                            <Badge className="bg-green-500/20 text-green-400 whitespace-nowrap">
                                                                                <Check className="h-3 w-3 mr-1" />
                                                                                Winner!
                                                                            </Badge>
                                                                            <p className="text-sm font-data text-green-400 mt-1 whitespace-nowrap">
                                                                                +{myPick.points_earned.toFixed(1)} pts
                                                                            </p>
                                                                            {myPick.win_points > 0 && myPick.place_points > 0 && (
                                                                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                                                                    (Win + Place)
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    ) : isPlaced ? (
                                                                        <div>
                                                                            <Badge className="bg-blue-500/20 text-blue-400 whitespace-nowrap">
                                                                                <Check className="h-3 w-3 mr-1" />
                                                                                {getOrdinal(myPick.result_position)} Place
                                                                            </Badge>
                                                                            <p className="text-sm font-data text-blue-400 mt-1 whitespace-nowrap">
                                                                                +{myPick.points_earned.toFixed(1)} pts
                                                                            </p>
                                                                            <p className="text-xs text-muted-foreground whitespace-nowrap">
                                                                                (1/4 odds)
                                                                            </p>
                                                                        </div>
                                                                    ) : myPick?.result_position ? (
                                                                        <div>
                                                                            <Badge variant="outline" className="text-muted-foreground whitespace-nowrap">
                                                                                {getOrdinal(myPick.result_position)}
                                                                            </Badge>
                                                                            <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">0 pts</p>
                                                                        </div>
                                                                    ) : race.status === 'finished' && myPick ? (
                                                                        <Badge className="bg-red-500/20 text-red-400 whitespace-nowrap">
                                                                            <X className="h-3 w-3 mr-1" />
                                                                            Lost
                                                                        </Badge>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Full Leaderboard Tab - Comprehensive View */}
                        <TabsContent value="leaderboard">
                            <div className="space-y-6">
                                {/* Top 3 Podium */}
                                {leaderboard.length >= 3 && (
                                    <Card className="overflow-hidden">
                                        <div className="bg-gradient-to-r from-amber-500/10 via-primary/5 to-amber-500/10 p-6">
                                            <div className="flex items-end justify-center gap-4 md:gap-8">
                                                {/* 2nd Place */}
                                                <div className="flex flex-col items-center">
                                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-white font-bold text-xl shadow-lg mb-2">
                                                        2
                                                    </div>
                                                    <p className="font-semibold text-sm text-center">{leaderboard[1]?.user_name}</p>
                                                    <p className="font-data font-bold text-lg">{leaderboard[1]?.total_points.toFixed(1)}</p>
                                                    <div className="h-16 w-20 bg-gradient-to-t from-gray-500 to-gray-300 rounded-t-lg mt-2 flex items-center justify-center">
                                                        <span className="text-white font-bold">2nd</span>
                                                    </div>
                                                </div>
                                                
                                                {/* 1st Place */}
                                                <div className="flex flex-col items-center">
                                                    <Crown className="h-6 w-6 text-amber-400 mb-1" />
                                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-amber-500/30 mb-2">
                                                        1
                                                    </div>
                                                    <p className="font-semibold text-center">{leaderboard[0]?.user_name}</p>
                                                    <p className="font-data font-bold text-xl text-amber-500">{leaderboard[0]?.total_points.toFixed(1)}</p>
                                                    <div className="h-24 w-24 bg-gradient-to-t from-amber-600 to-amber-400 rounded-t-lg mt-2 flex items-center justify-center">
                                                        <span className="text-white font-bold text-lg">1st</span>
                                                    </div>
                                                </div>
                                                
                                                {/* 3rd Place */}
                                                <div className="flex flex-col items-center">
                                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-white font-bold text-xl shadow-lg mb-2">
                                                        3
                                                    </div>
                                                    <p className="font-semibold text-sm text-center">{leaderboard[2]?.user_name}</p>
                                                    <p className="font-data font-bold text-lg">{leaderboard[2]?.total_points.toFixed(1)}</p>
                                                    <div className="h-12 w-20 bg-gradient-to-t from-amber-800 to-amber-600 rounded-t-lg mt-2 flex items-center justify-center">
                                                        <span className="text-white font-bold">3rd</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                )}

                                {/* Full Standings with Day-by-Day Breakdown */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <ListOrdered className="h-5 w-5 text-primary" />
                                            Full Standings
                                        </CardTitle>
                                        <CardDescription>
                                            {leaderboard.length} competitors • {leaderboardDateFilter ? `Day: ${format(new Date(leaderboardDateFilter), 'EEE d MMM')}` : 'Overall'} • Click a player to expand
                                        </CardDescription>
                                        
                                        {/* Day Filter Tabs */}
                                        {availableDates.length > 1 && (
                                            <div className="flex flex-wrap gap-2 mt-4">
                                                <Button
                                                    variant={leaderboardDateFilter === null ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => setLeaderboardDateFilter(null)}
                                                    className="text-xs"
                                                >
                                                    Overall
                                                </Button>
                                                {availableDates.map((date, idx) => (
                                                    <Button
                                                        key={date}
                                                        variant={leaderboardDateFilter === date ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => setLeaderboardDateFilter(date)}
                                                        className="text-xs"
                                                    >
                                                        Day {idx + 1}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        {/* Player Rows */}
                                        <div className="divide-y">
                                            {leaderboard.map((entry, idx) => {
                                                const isCurrentUser = entry.user_id === user?.id;
                                                const isExpanded = expandedPlayer === entry.user_id;
                                                
                                                // Group race form by day (computed inline, no useMemo needed as it's quick)
                                                const racesByDay = (() => {
                                                    if (!entry.race_form || !raceHeaders) return {};
                                                    
                                                    const grouped = {};
                                                    entry.race_form.forEach((form, formIdx) => {
                                                        const raceInfo = raceHeaders[formIdx];
                                                        if (!raceInfo) return;
                                                        
                                                        const raceDate = raceInfo.race_date || raceInfo.race_time?.split('T')[0] || 'Unknown';
                                                        const stageName = raceInfo.stage_name || competition?.meeting_name || 'Races';
                                                        const dayKey = `${raceDate}|${stageName}`;
                                                        
                                                        if (!grouped[dayKey]) {
                                                            grouped[dayKey] = {
                                                                date: raceDate,
                                                                stageName: stageName,
                                                                races: [],
                                                                totalPoints: 0,
                                                                winners: 0,
                                                                places: 0
                                                            };
                                                        }
                                                        
                                                        grouped[dayKey].races.push({ form, raceInfo, globalIdx: formIdx });
                                                        if (form.points_earned > 0) {
                                                            grouped[dayKey].totalPoints += form.points_earned;
                                                        }
                                                        if (form.result_position === 1) grouped[dayKey].winners++;
                                                        if (form.is_placed && form.result_position !== 1) grouped[dayKey].places++;
                                                    });
                                                    
                                                    return grouped;
                                                })();
                                                
                                                const sortedDays = Object.entries(racesByDay).sort(([a], [b]) => a.localeCompare(b));
                                                
                                                return (
                                                    <div key={entry.user_id}>
                                                        {/* Main Row - Compact Summary */}
                                                        <div 
                                                            className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                                                                isCurrentUser ? 'bg-primary/10' : 'hover:bg-muted/30'
                                                            } ${isExpanded ? 'bg-muted/50' : ''}`}
                                                            onClick={() => setExpandedPlayer(isExpanded ? null : entry.user_id)}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                {/* Rank */}
                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                                                                    idx === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' :
                                                                    idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                                                    idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800 text-white' :
                                                                    'bg-muted text-muted-foreground'
                                                                }`}>
                                                                    {entry.rank}
                                                                </div>
                                                                
                                                                {/* Player Info */}
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`font-medium ${isCurrentUser ? 'text-primary' : ''}`}>
                                                                            {entry.user_name}
                                                                        </span>
                                                                        {entry.daily_winner_badges?.length > 0 && (
                                                                            <div className="flex gap-0.5">
                                                                                {entry.daily_winner_badges.map((date, i) => (
                                                                                    <Medal key={i} className="h-3 w-3 text-amber-400" title={`Day winner: ${date}`} />
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {entry.company && (
                                                                        <div className="text-xs text-muted-foreground">{entry.company}</div>
                                                                    )}
                                                                    <div className="text-xs text-muted-foreground">
                                                                        <span className="text-green-400">{entry.correct_picks}W</span>
                                                                        <span className="text-blue-400">/{entry.placed_picks || 0}P</span>
                                                                        <span className="text-muted-foreground">/{entry.picks_made || 0} picks</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex items-center gap-3">
                                                                {/* Quick Day Summary Pills */}
                                                                <div className="hidden sm:flex gap-1">
                                                                    {sortedDays.slice(0, 4).map(([dayKey, dayData]) => {
                                                                        const finishedCount = dayData.races.filter(r => r.raceInfo?.status === 'finished').length;
                                                                        const hasResults = finishedCount > 0;
                                                                        return (
                                                                            <div 
                                                                                key={dayKey}
                                                                                className={`px-2 py-1 rounded text-xs ${
                                                                                    dayData.winners > 0 ? 'bg-amber-500/20 text-amber-400' :
                                                                                    dayData.places > 0 ? 'bg-blue-500/20 text-blue-400' :
                                                                                    hasResults ? 'bg-muted text-muted-foreground' :
                                                                                    'bg-muted/50 text-muted-foreground/50'
                                                                                }`}
                                                                                title={`${dayData.stageName}: ${dayData.totalPoints.toFixed(1)} pts`}
                                                                            >
                                                                                {dayData.totalPoints > 0 ? `+${dayData.totalPoints.toFixed(1)}` : '0'}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {sortedDays.length > 4 && (
                                                                        <div className="px-2 py-1 rounded text-xs bg-muted/50 text-muted-foreground">
                                                                            +{sortedDays.length - 4}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Total Points */}
                                                                <div className="text-right min-w-[60px]">
                                                                    <span className="font-data font-bold text-lg">{entry.total_points.toFixed(1)}</span>
                                                                </div>
                                                                
                                                                {/* Expand indicator */}
                                                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Expanded Details - Day by Day */}
                                                        {isExpanded && (
                                                            <div className="bg-muted/20 border-t">
                                                                {sortedDays.map(([dayKey, dayData], dayIdx) => {
                                                                    const dayDate = dayData.date;
                                                                    const formattedDate = dayDate !== 'Unknown' 
                                                                        ? format(new Date(dayDate), 'EEE d MMM')
                                                                        : 'Unknown Date';
                                                                    const finishedCount = dayData.races.filter(r => r.raceInfo?.status === 'finished').length;
                                                                    
                                                                    return (
                                                                        <div key={dayKey} className={`${dayIdx > 0 ? 'border-t border-border/50' : ''}`}>
                                                                            {/* Day Header */}
                                                                            <div className="px-4 py-2 bg-muted/30 flex items-center justify-between">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4 text-primary" />
                                                                                    <span className="font-semibold text-sm">{dayData.stageName}</span>
                                                                                    <span className="text-xs text-muted-foreground">• {formattedDate}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-3 text-xs">
                                                                                    <span className="text-muted-foreground">{finishedCount}/{dayData.races.length} races</span>
                                                                                    {dayData.winners > 0 && <span className="text-amber-400">{dayData.winners}W</span>}
                                                                                    {dayData.places > 0 && <span className="text-blue-400">{dayData.places}P</span>}
                                                                                    <span className={`font-data font-semibold ${dayData.totalPoints > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                                                                                        {dayData.totalPoints > 0 ? `+${dayData.totalPoints.toFixed(1)}` : '0'} pts
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            {/* Race Grid for this Day */}
                                                                            <div className="px-4 py-3">
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {dayData.races.map(({ form, raceInfo, globalIdx }) => {
                                                                                        const isFinished = raceInfo?.status === 'finished';
                                                                                        const raceStartTime = raceInfo?.race_time ? new Date(raceInfo.race_time) : null;
                                                                                        const isRaceLocked = raceStartTime ? raceStartTime <= new Date() : false;
                                                                                        const isOwnEntry = entry.user_id === userEntry?.user_id;
                                                                                        const shouldHidePick = !isOwnEntry && !isFinished && !isRaceLocked && form.picked;
                                                                                        
                                                                                        const raceTime = raceInfo?.race_time 
                                                                                            ? format(new Date(raceInfo.race_time), 'HH:mm')
                                                                                            : '';
                                                                                        
                                                                                        // Determine style
                                                                                        let bgStyle = 'bg-muted/50';
                                                                                        let borderStyle = 'border-muted';
                                                                                        let resultBadge = null;
                                                                                        
                                                                                        if (!form.picked) {
                                                                                            bgStyle = 'bg-muted/30';
                                                                                            borderStyle = 'border-dashed border-muted-foreground/30';
                                                                                        } else if (shouldHidePick) {
                                                                                            bgStyle = 'bg-primary/10';
                                                                                            borderStyle = 'border-primary/30';
                                                                                        } else if (isFinished) {
                                                                                            if (form.result_position === 1) {
                                                                                                bgStyle = 'bg-gradient-to-br from-amber-400/20 to-amber-600/20';
                                                                                                borderStyle = 'border-amber-500';
                                                                                                resultBadge = <Badge className="bg-amber-500 text-black text-[10px] px-1">1st</Badge>;
                                                                                            } else if (form.is_placed) {
                                                                                                bgStyle = 'bg-blue-500/20';
                                                                                                borderStyle = 'border-blue-500/50';
                                                                                                resultBadge = <Badge className="bg-blue-500 text-white text-[10px] px-1">{getOrdinal(form.result_position)}</Badge>;
                                                                                            } else {
                                                                                                bgStyle = 'bg-red-500/10';
                                                                                                borderStyle = 'border-red-500/30';
                                                                                                resultBadge = <Badge variant="outline" className="text-red-400 text-[10px] px-1 border-red-500/30">L</Badge>;
                                                                                            }
                                                                                        }
                                                                                        
                                                                                        return (
                                                                                            <TooltipProvider key={globalIdx} delayDuration={100}>
                                                                                                <Tooltip>
                                                                                                    <TooltipTrigger asChild>
                                                                                                        <div className={`p-2 rounded-lg border ${bgStyle} ${borderStyle} min-w-[100px] cursor-help`}>
                                                                                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                                                                                <span className="text-[10px] text-muted-foreground font-data">{raceTime}</span>
                                                                                                                {resultBadge}
                                                                                                            </div>
                                                                                                            <p className="text-xs font-medium truncate max-w-[120px]">
                                                                                                                {shouldHidePick ? (
                                                                                                                    <span className="text-primary/60 italic flex items-center gap-1">
                                                                                                                        <Lock className="h-3 w-3" /> Hidden
                                                                                                                    </span>
                                                                                                                ) : form.picked ? (
                                                                                                                    form.entrant_name
                                                                                                                ) : (
                                                                                                                    <span className="text-muted-foreground italic">No pick</span>
                                                                                                                )}
                                                                                                            </p>
                                                                                                            {form.points_earned > 0 && (
                                                                                                                <p className="text-[10px] text-green-400 font-data font-semibold">
                                                                                                                    +{form.points_earned.toFixed(1)} pts
                                                                                                                </p>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </TooltipTrigger>
                                                                                                    <TooltipContent side="top" className="bg-background border max-w-[250px]">
                                                                                                        <p className="font-semibold text-sm">{raceInfo?.event_title}</p>
                                                                                                        {form.picked && !shouldHidePick && (
                                                                                                            <p className="text-xs text-muted-foreground">Pick: {form.entrant_name}</p>
                                                                                                        )}
                                                                                                        {form.was_non_runner && (
                                                                                                            <p className="text-xs text-amber-500">NR → {form.substitute_name || 'Favorite'}</p>
                                                                                                        )}
                                                                                                    </TooltipContent>
                                                                                                </Tooltip>
                                                                                            </TooltipProvider>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                
                                                                {/* Overall Summary */}
                                                                <div className="px-4 py-3 border-t bg-muted/30 flex items-center justify-between">
                                                                    <div className="flex gap-4 text-sm">
                                                                        <span><strong className="text-green-400">{entry.correct_picks}</strong> Winners</span>
                                                                        <span><strong className="text-blue-400">{entry.placed_picks || 0}</strong> Places</span>
                                                                        <span><strong>{entry.picks_made}</strong> Picks</span>
                                                                    </div>
                                                                    <div className="font-bold font-data text-lg">
                                                                        Total: <span className="text-primary">{entry.total_points.toFixed(1)}</span> pts
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Race Results Summary */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Trophy className="h-5 w-5 text-amber-400" />
                                            Race Results
                                        </CardTitle>
                                        <CardDescription>
                                            Winners, starting prices, and points available
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <div className="divide-y">
                                            {competition.races?.map((race, idx) => {
                                                const isFinished = race.status === 'finished';
                                                return (
                                                    <div 
                                                        key={race.event_id} 
                                                        className={`p-4 ${!isFinished ? 'opacity-60' : ''}`}
                                                    >
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-sm font-data text-muted-foreground">
                                                                        R{idx + 1}
                                                                    </span>
                                                                    <Badge className={
                                                                        isFinished ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'
                                                                    }>
                                                                        {isFinished ? 'Result' : 'Pending'}
                                                                    </Badge>
                                                                </div>
                                                                <p className="font-medium truncate">{race.event_title}</p>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {format(parseISO(race.race_time), 'HH:mm')} • {race.stage_name}
                                                                </p>
                                                            </div>
                                                            {isFinished && race.winning_entrant_name && (
                                                                <div className="text-right shrink-0">
                                                                    <div className="flex items-center gap-2 justify-end">
                                                                        <Trophy className="h-4 w-4 text-amber-400" />
                                                                        <span className="font-semibold">{race.winning_entrant_name}</span>
                                                                    </div>
                                                                    {/* Use stored points_earned from winner's result */}
                                                                    {race.results && race.results[0]?.points_earned > 0 && (
                                                                        <div className="mt-1">
                                                                            <span className="text-green-400 font-data text-sm">
                                                                                +{race.results[0].points_earned.toFixed(1)} pts
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {race.results && race.results.length >= 3 && (
                                                                        <div className="mt-2 text-xs text-muted-foreground">
                                                                            <span className="text-gray-400">2nd:</span> {race.results[1]?.entrant_name}
                                                                            <span className="mx-1">•</span>
                                                                            <span className="text-amber-700">3rd:</span> {race.results[2]?.entrant_name}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Stats Overview */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Zap className="h-5 w-5 text-primary" />
                                            Competition Stats
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                                <p className="text-3xl font-bold font-data text-primary">{leaderboard.length}</p>
                                                <p className="text-sm text-muted-foreground">Players</p>
                                            </div>
                                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                                <p className="text-3xl font-bold font-data text-amber-400">{sortedRaces.length}</p>
                                                <p className="text-sm text-muted-foreground">Total Races</p>
                                            </div>
                                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                                <p className="text-3xl font-bold font-data text-green-400">{finishedRaces.length}</p>
                                                <p className="text-sm text-muted-foreground">Completed</p>
                                            </div>
                                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                                <p className="text-3xl font-bold font-data">{leaderboard[0]?.total_points.toFixed(1) || 0}</p>
                                                <p className="text-sm text-muted-foreground">Top Score</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Leaderboard Sidebar */}
                <div>
                    {/* How Scoring Works - Educational Card */}
                    <HowScoringWorks />

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Medal className="h-5 w-5 text-amber-400" />
                                Leaderboard
                            </CardTitle>
                            <CardDescription>{leaderboard.length} competitors</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ScrollArea className="h-[500px]">
                                <div className="p-4 space-y-2">
                                    {leaderboard.map((entry, idx) => (
                                        <div 
                                            key={entry.user_id}
                                            className={`p-3 rounded-lg flex items-center gap-3 ${
                                                entry.user_id === user?.id 
                                                    ? 'bg-primary/10 border border-primary/30' 
                                                    : 'bg-muted/30'
                                            }`}
                                            data-testid={`leaderboard-entry-${idx}`}
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                                idx === 0 ? 'bg-amber-500 text-black' :
                                                idx === 1 ? 'bg-gray-400 text-black' :
                                                idx === 2 ? 'bg-amber-700 text-white' :
                                                'bg-muted text-muted-foreground'
                                            }`}>
                                                {entry.rank}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{entry.user_name}</p>
                                                {entry.company && (
                                                    <p className="text-xs text-muted-foreground truncate">{entry.company}</p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    <span className="text-green-400">{entry.correct_picks}W</span>
                                                    {entry.placed_picks > 0 && (
                                                        <span className="text-blue-400 ml-1">/{entry.placed_picks}P</span>
                                                    )}
                                                    <span className="ml-1">/ {entry.picks_made} picks</span>
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold font-data">{entry.total_points.toFixed(1)}</p>
                                                {entry.rank_change !== 0 && entry.rank_change !== undefined && (
                                                    <p className={`text-xs flex items-center justify-end ${
                                                        entry.rank_change > 0 ? 'text-green-400' : 'text-red-400'
                                                    }`}>
                                                        {entry.rank_change > 0 ? (
                                                            <><TrendingUp className="h-3 w-3" />+{entry.rank_change}</>
                                                        ) : (
                                                            <><TrendingDown className="h-3 w-3" />{entry.rank_change}</>
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                            {entry.daily_winner_badges?.length > 0 && (
                                                <Medal className="h-4 w-4 text-amber-400" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                    
                    {/* Admin Daily Winners Management */}
                    {user?.is_admin && (
                        <AdminDailyWinnersPanel 
                            competitionId={competitionId} 
                            leaderboard={leaderboard}
                            competition={competition}
                        />
                    )}
                </div>
            </div>

            {/* Pick Selection Dialog - Enhanced with full race card info */}
            <Dialog open={!!selectedRace} onOpenChange={(open) => !open && setSelectedRace(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="text-xl">{selectedRace?.event_title}</DialogTitle>
                        <DialogDescription>
                            {(() => {
                                const raceTime = new Date(selectedRace?.race_time);
                                const now = new Date();
                                
                                // Calculate declaration datetime: 10am, 2 days before race date
                                const declarationDate = new Date(raceTime);
                                declarationDate.setDate(declarationDate.getDate() - 2);
                                declarationDate.setHours(10, 0, 0, 0);
                                const entriesNotDeclared = now < declarationDate;
                                
                                if (entriesNotDeclared && raceEntrants.length > 0) {
                                    const declarationDateStr = declarationDate.toLocaleDateString('en-GB', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long'
                                    });
                                    
                                    return (
                                        <span className="text-amber-400">
                                            These are preliminary entries. Final declarations on {declarationDateStr} at 10am. Picks will be available after declarations.
                                        </span>
                                    );
                                }
                                
                                return (
                                    <>
                                        Select your pick for this race. 
                                        {competition.my_picks?.[selectedRace?.event_id] && competition.my_picks[selectedRace?.event_id].pick_is_non_runner && (
                                            <span className="text-red-400"> Your pick is a non-runner - select a new horse.</span>
                                        )}
                                        {competition.my_picks?.[selectedRace?.event_id] && !competition.my_picks[selectedRace?.event_id].change_used && !competition.my_picks[selectedRace?.event_id].pick_is_non_runner && (
                                            <span className="text-amber-400"> You can change your pick once.</span>
                                        )}
                                    </>
                                );
                            })()}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {loadingEntrants ? (
                        <div className="space-y-3 py-4">
                            {[1, 2, 3, 4, 5].map(i => (
                                <Skeleton key={i} className="h-24 w-full" />
                            ))}
                        </div>
                    ) : (
                        <ScrollArea className="max-h-[60vh]">
                            <div className="space-y-4 py-2 pr-4">
                                {/* Official Results Section - For finished races */}
                                {(selectedRace?.status === 'finished' || raceResults) && (raceResults || selectedRace?.results) && (
                                    <Card className="border-green-500/30 bg-green-500/5">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="flex items-center gap-2 text-base">
                                                <Trophy className="h-5 w-5 text-amber-500" />
                                                Official Results
                                                <Badge className="bg-green-500/20 text-green-500 text-xs">Final</Badge>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {(raceResults || selectedRace?.results || []).slice(0, 4).map((result) => {
                                                const positionColors = {
                                                    1: 'bg-amber-500 text-black',
                                                    2: 'bg-gray-400 text-black',
                                                    3: 'bg-amber-700 text-white',
                                                    4: 'bg-muted text-muted-foreground'
                                                };
                                                const myPick = competition.my_picks?.[selectedRace.event_id];
                                                const isMyPick = myPick?.entrant_id === result.entrant_id;
                                                
                                                return (
                                                    <div 
                                                        key={result.entrant_id}
                                                        className={`p-3 rounded-lg ${
                                                            result.position === 1 
                                                                ? 'bg-amber-500/10 border border-amber-500/30' 
                                                                : 'bg-muted/30'
                                                        } ${isMyPick ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${positionColors[result.position] || positionColors[4]}`}>
                                                                {result.position}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-semibold truncate">
                                                                        {result.entrant_name}
                                                                    </span>
                                                                    {result.position === 1 && (
                                                                        <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
                                                                    )}
                                                                    {isMyPick && (
                                                                        <Badge className="bg-primary/20 text-primary text-xs shrink-0">
                                                                            Your Pick
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {result.jockey && (
                                                                    <div className="text-xs text-muted-foreground">
                                                                        {result.jockey}
                                                                        {result.beaten_distance && result.position > 1 && (
                                                                            <span className="ml-2">• beaten {result.beaten_distance}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-right shrink-0">
                                                                <p className="font-data font-bold">
                                                                    {result.sp_fractional || 'SP'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {/* Finishing Commentary - Show for top 3 */}
                                                        {(result.finishing_comment || result.closeup_comment) && result.position <= 3 && (
                                                            <div className="mt-2 pt-2 border-t border-border/30">
                                                                <p className="text-xs text-muted-foreground leading-relaxed italic">
                                                                    "{result.finishing_comment || result.closeup_comment}"
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {selectedRace?.places_paid > 0 && (
                                                <p className="text-xs text-muted-foreground text-center pt-2">
                                                    Each-way: {selectedRace.places_paid} places paid (1/4 odds)
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                
                                {/* Picks Distribution - shown after deadline */}
                                {picksDistribution && picksDistribution.total_picks > 0 && (
                                    <Card className="border-blue-500/30 bg-blue-500/5">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="flex items-center gap-2 text-base">
                                                <Users className="h-5 w-5 text-blue-500" />
                                                Competition Picks
                                                <Badge variant="outline" className="ml-2">{picksDistribution.total_picks} picks</Badge>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            <p className="text-xs text-muted-foreground">
                                                How other competitors have picked for this race
                                            </p>
                                            
                                            {/* Top 3 Picks */}
                                            <div className="space-y-2">
                                                {picksDistribution.distribution.slice(0, 3).map((pick, idx) => (
                                                    <div key={pick.entrant_id} className="flex items-center gap-3">
                                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                            idx === 0 ? 'bg-amber-500 text-black' :
                                                            idx === 1 ? 'bg-gray-400 text-black' :
                                                            'bg-amber-700 text-white'
                                                        }`}>
                                                            {idx + 1}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="font-medium text-sm">{pick.entrant_name}</span>
                                                                <span className="text-sm font-bold">{pick.percentage}%</span>
                                                            </div>
                                                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                                <div 
                                                                    className={`h-full rounded-full ${
                                                                        idx === 0 ? 'bg-amber-500' :
                                                                        idx === 1 ? 'bg-gray-400' :
                                                                        'bg-amber-700'
                                                                    }`}
                                                                    style={{ width: `${pick.percentage}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            {/* Other Picks (collapsible) */}
                                            {picksDistribution.distribution.length > 3 && (
                                                <div className="pt-2 border-t border-border/50">
                                                    <button
                                                        onClick={() => setShowOtherPicks(!showOtherPicks)}
                                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
                                                    >
                                                        <ChevronDown className={`h-4 w-4 transition-transform ${showOtherPicks ? 'rotate-180' : ''}`} />
                                                        {showOtherPicks ? 'Hide' : 'Show'} other picks ({picksDistribution.distribution.length - 3})
                                                    </button>
                                                    
                                                    {showOtherPicks && (
                                                        <div className="mt-3 space-y-2">
                                                            {picksDistribution.distribution.slice(3).map((pick, idx) => (
                                                                <div key={pick.entrant_id} className="flex items-center gap-3">
                                                                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                                                        {idx + 4}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center justify-between mb-1">
                                                                            <span className="text-sm text-muted-foreground">{pick.entrant_name}</span>
                                                                            <span className="text-sm">{pick.percentage}%</span>
                                                                        </div>
                                                                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                                            <div 
                                                                                className="h-full bg-muted-foreground/50 rounded-full"
                                                                                style={{ width: `${pick.percentage}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {/* Entrants List */}
                                <div className="space-y-4">
                                {raceEntrants.length === 0 && !loadingEntrants ? (
                                    // Show "not declared yet" message
                                    (() => {
                                        const raceTime = new Date(selectedRace?.race_time);
                                        const now = new Date();
                                        
                                        // Calculate declaration datetime: 10am, 2 days before race date
                                        const declarationDate = new Date(raceTime);
                                        declarationDate.setDate(declarationDate.getDate() - 2);
                                        declarationDate.setHours(10, 0, 0, 0);
                                        
                                        const entriesNotDeclared = now < declarationDate;
                                        
                                        const declarationDateStr = declarationDate.toLocaleDateString('en-GB', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long'
                                        });
                                        
                                        if (entriesNotDeclared) {
                                            return (
                                                <Card className="border-amber-500/30 bg-amber-500/5">
                                                    <CardContent className="pt-6">
                                                        <div className="text-center py-8">
                                                            <Clock className="h-12 w-12 mx-auto text-amber-500 mb-4" />
                                                            <h3 className="font-heading text-lg font-semibold mb-2">
                                                                Entries Not Yet Declared
                                                            </h3>
                                                            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                                                                Final declarations for this race will be made at <strong>10:00 AM</strong> on:
                                                            </p>
                                                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-4 py-2 text-base">
                                                                {declarationDateStr}
                                                            </Badge>
                                                            <p className="text-sm text-muted-foreground mt-4">
                                                                Check back after declarations to make your pick!
                                                            </p>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        } else {
                                            return (
                                                <Card className="border-muted">
                                                    <CardContent className="pt-6">
                                                        <div className="text-center py-8">
                                                            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                                            <h3 className="font-heading text-lg font-semibold mb-2">
                                                                No Entries Available
                                                            </h3>
                                                            <p className="text-muted-foreground">
                                                                Unable to load entries for this race. Please try again later.
                                                            </p>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        }
                                    })()
                                ) : (
                                    (() => {
                                        // Check if entries are declared
                                        // Declarations happen at 10am, 2 days before the race DATE
                                        const raceTime = new Date(selectedRace?.race_time);
                                        const now = new Date();
                                        
                                        // Calculate declaration datetime
                                        const declarationDate = new Date(raceTime);
                                        declarationDate.setDate(declarationDate.getDate() - 2);
                                        declarationDate.setHours(10, 0, 0, 0);
                                        
                                        const entriesNotDeclared = now < declarationDate;
                                        
                                        const declarationDateStr = declarationDate.toLocaleDateString('en-GB', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short'
                                        });
                                        
                                        return raceEntrants.map(entrant => {
                                            const isPicked = competition.my_picks?.[selectedRace?.event_id]?.entrant_id === entrant.id;
                                            const isExpertPick = expertTips.some(t => t.entrant_name === entrant.name);
                                            const expertTipType = expertTips.find(t => t.entrant_name === entrant.name)?.type;
                                            const isNonRunner = entrant.status === 'NON_RUNNER' || entrant.status === 'DOUBTFUL';
                                            
                                            return (
                                                <EnhancedEntrantCard
                                                    key={entrant.id}
                                                    entrant={entrant}
                                                    isPicked={isPicked}
                                                    isExpertPick={isExpertPick}
                                                    expertTipType={expertTipType}
                                                    isNonRunner={isNonRunner}
                                                    aiPicks={aiPicks}
                                                    submittingPick={submittingPick}
                                                    onSelect={submitPick}
                                                    disabled={entriesNotDeclared}
                                                    disabledReason={`Picks available after declarations on ${declarationDateStr} at 10am`}
                                                />
                                            );
                                        });
                                    })()
                                )}
                                </div>
                            </div>
                        </ScrollArea>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedRace(null)}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Invite Others to Compete</DialogTitle>
                        <DialogDescription>
                            Share this link with others to invite them to join the competition
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}${inviteLinkPath}`}
                            className="flex-1 bg-transparent text-sm outline-none"
                        />
                        <Button size="sm" onClick={copyInviteLink}>
                            <Copy className="h-4 w-4" />
                        </Button>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowShareDialog(false)}>Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ScoringRulesModal open={showRulesModal} onOpenChange={setShowRulesModal} />
        </div>
    );
}
